import { ArrowLeft, Upload, Copy, Check, TrendingUp, TrendingDown, Minus, Maximize2, X, Plus, Pencil, Trash2, Sparkles } from 'lucide-react';
import { WaveformViewer, WaveformJumpTo } from './WaveformViewer';
import { AnomalyScoreChart } from './AnomalyScoreChart';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';
import { useState, useRef, useEffect, useMemo, Fragment } from 'react';
import { PointDetail } from './PointDetail';
import { SensorEditModal } from './SensorEditModal';
import { apiFetch } from '../api/client';

interface PointData {
  V: number;
  H: number;
  A: number;
}

interface Thresholds {
  yellow: number;
  red: number;
}

interface Point {
  id: string;
  name: string;
  velocity: PointData;
  acceleration: PointData;
  envelope: PointData;
  temperature: PointData;
  status: 'low' | 'medium' | 'high';
  velocityThresholds?: Thresholds;
  accelerationThresholds?: Thresholds;
  envelopeThresholds?: Thresholds;
  temperatureThresholds?: Thresholds;
  machineId?: string;
  axisIds?: number[];
  sensorSerial?: string;
  batteryVoltage?: number; // Battery voltage from 2.0 to 3.0
  isConnected?: boolean; // Whether the sensor is connected
  lastDataTimestamp?: Date; // Last time data was received (for disconnected sensors)
  axisMapping?: { // Sensor axis to H/V/A mapping
    X: 'H' | 'V' | 'A';
    Y: 'H' | 'V' | 'A';
    Z: 'H' | 'V' | 'A';
  };
}

interface MachineDetailProps {
  machineId: string;
  machineName: string;
  points: Point[];
  initialPointId?: string;
  initialPointName?: string;
  initialAxisNum?: number;
  imageUrl?: string;
  onBack: () => void;
  onImageUpload?: (machineId: string, imageUrl: string) => void;
}

interface AlarmEvent {
  id: string;
  pointName: string;
  metric: string;
  value: number;
  threshold: number;
  severity: 'medium' | 'high';
  timestamp: string; // 'YYYY/MM/DD HH:MM:SS' Eastern
  description: string;
}

interface MachineHealthScore {
  machine_id: string;
  health_score: number;
  health_label: string;
  health_colour: string;
  alarm_component: number;
  peakvue_alarm_component: number;
  spectral_component: number;
  roc_component: number;
  peakvue_trend_component: number;
  raw_score_100: number;
  primary_driver: string | null;
  mitigating_factor: string | null;
  alarm_detail: string | null;
  peakvue_alarm_detail: string | null;
  spectral_detail: string | null;
  roc_detail: string | null;
  peakvue_trend_detail: string | null;
  alarm_count_90d: number;
  has_waveform_data: boolean;
  has_roc_data: boolean;
}

// Format 'YYYY/MM/DD HH:MM:SS' Eastern timestamps for display (no timezone conversion needed).
function fmtEastern(ts: string, showDate = true, showTime = true): string {
  if (!ts) return '';
  const [datePart, timePart = '00:00:00'] = ts.split(' ');
  const sep = datePart.includes('/') ? '/' : '-';
  const [y, m, d] = datePart.split(sep).map(Number);
  const [hh, mm] = timePart.split(':').map(Number);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const ampm = hh >= 12 ? 'PM' : 'AM';
  const h12  = hh % 12 || 12;
  if (showDate && showTime) return `${months[m-1]} ${d}, ${h12}:${String(mm).padStart(2,'0')} ${ampm}`;
  if (showDate)             return `${months[m-1]} ${d}, ${y}`;
  return `${h12}:${String(mm).padStart(2,'0')} ${ampm}`;
}

// Format UTC timestamps (e.g. from devices.last_data_at, reports.created_at) as Eastern.
// Appends Z if no timezone offset is present so the browser always treats the value as UTC.
function fmtUtcToEastern(ts: string): string {
  if (!ts) return '';
  let s = ts.trim().replace(' ', 'T');
  if (!/Z$|[+-]\d{2}:?\d{2}$/.test(s)) s += 'Z';
  const d = new Date(s);
  if (isNaN(d.getTime())) return ts;
  return d.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// Generate alarm history for the last 45 days
const generateAlarmHistory = (points: Point[]): AlarmEvent[] => {
  const alarms: AlarmEvent[] = [];
  const now = new Date();
  
  points.forEach(point => {
    // Generate 1-5 random alarms per point over the last 45 days
    const alarmCount = Math.floor(Math.random() * 5) + 1;
    
    for (let i = 0; i < alarmCount; i++) {
      const daysAgo = Math.floor(Math.random() * 45);
      const d = new Date(now);
      d.setDate(d.getDate() - daysAgo);
      const timestamp = `${d.getFullYear()}/${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')} 00:00:00`;
      
      const metrics = ['velocity', 'acceleration', 'envelope', 'temperature'];
      const metric = metrics[Math.floor(Math.random() * metrics.length)];
      const severity = Math.random() > 0.6 ? 'high' : 'medium';
      
      let value: number;
      let threshold: number;
      let unit: string;
      
      switch (metric) {
        case 'velocity':
          value = point.velocity.V * (1.2 + Math.random() * 0.3);
          threshold = severity === 'high' ? 0.4 : 0.3;
          unit = 'in/s';
          break;
        case 'acceleration':
          value = point.acceleration.V * (1.2 + Math.random() * 0.3);
          threshold = severity === 'high' ? 2.0 : 1.5;
          unit = "G's";
          break;
        case 'envelope':
          value = point.envelope.V * (1.2 + Math.random() * 0.3);
          threshold = severity === 'high' ? 3.0 : 2.5;
          unit = "G's";
          break;
        case 'temperature':
          value = point.temperature.V * (1.1 + Math.random() * 0.2);
          threshold = severity === 'high' ? 180 : 160;
          unit = '°F';
          break;
        default:
          value = 0;
          threshold = 0;
          unit = '';
      }
      
      const metricName = metric.charAt(0).toUpperCase() + metric.slice(1);
      const severityText = severity === 'high' ? 'Critical' : 'Warning';
      
      alarms.push({
        id: `${point.id}-${metric}-${i}`,
        pointName: point.name,
        metric: metricName,
        value: parseFloat(value.toFixed(2)),
        threshold: parseFloat(threshold.toFixed(2)),
        severity,
        timestamp,
        description: `${severityText}: ${metricName} exceeded ${severity === 'high' ? 'critical' : 'warning'} threshold (${threshold} ${unit}) at ${point.name}`
      });
    }
  });
  
  // Sort by timestamp, most recent first
  return alarms.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
};

interface RateOfChange {
  pointName: string;
  velocity: { current: number; average: number; change: number; percentChange: number };
  acceleration: { current: number; average: number; change: number; percentChange: number };
  envelope: { current: number; average: number; change: number; percentChange: number };
  temperature: { current: number; average: number; change: number; percentChange: number };
}

// Calculate rate of change over 45-day rolling average
const calculateRateOfChange = (points: Point[]): RateOfChange[] => {
  return points.map(point => {
    // Simulate 45-day averages (in real app, this would come from historical data)
    const velocityAvg = point.velocity.V * (0.85 + Math.random() * 0.15);
    const accelerationAvg = point.acceleration.V * (0.88 + Math.random() * 0.12);
    const envelopeAvg = point.envelope.V * (0.82 + Math.random() * 0.18);
    const temperatureAvg = point.temperature.V * (0.92 + Math.random() * 0.08);
    
    const velocityChange = point.velocity.V - velocityAvg;
    const accelerationChange = point.acceleration.V - accelerationAvg;
    const envelopeChange = point.envelope.V - envelopeAvg;
    const temperatureChange = point.temperature.V - temperatureAvg;
    
    return {
      pointName: point.name,
      velocity: {
        current: point.velocity.V,
        average: velocityAvg,
        change: velocityChange,
        percentChange: (velocityChange / velocityAvg) * 100
      },
      acceleration: {
        current: point.acceleration.V,
        average: accelerationAvg,
        change: accelerationChange,
        percentChange: (accelerationChange / accelerationAvg) * 100
      },
      envelope: {
        current: point.envelope.V,
        average: envelopeAvg,
        change: envelopeChange,
        percentChange: (envelopeChange / envelopeAvg) * 100
      },
      temperature: {
        current: point.temperature.V,
        average: temperatureAvg,
        change: temperatureChange,
        percentChange: (temperatureChange / temperatureAvg) * 100
      }
    };
  });
};

// Generate historical data for a specific metric
const generateHistoricalData = (points: Point[], metric: 'velocity' | 'acceleration' | 'envelope' | 'temperature', timeRange: number) => {
  let timeStamps: string[];
  let dataPointCount: number;
  
  // timeRange: 0 = hours, 1 = days, 2 = weeks, 3 = months
  switch (timeRange) {
    case 0: // Hours (24 hours)
      timeStamps = ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '24:00'];
      dataPointCount = 7;
      break;
    case 1: // Days (7 days)
      timeStamps = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      dataPointCount = 7;
      break;
    case 2: // Weeks (4 weeks)
      timeStamps = ['Week 1', 'Week 2', 'Week 3', 'Week 4'];
      dataPointCount = 4;
      break;
    case 3: // Months (6 months)
      timeStamps = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
      dataPointCount = 6;
      break;
    default:
      timeStamps = ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '24:00'];
      dataPointCount = 7;
  }
  
  return timeStamps.map((time, index) => {
    const dataPoint: any = { time };
    
    points.forEach(point => {
      // Generate varying multipliers based on index
      const multiplier = 0.8 + (Math.sin(index * 0.5) * 0.2) + (index % 2 === 0 ? 0.1 : 0);
      // Use the V (vertical) value as the representative value for the aggregate chart
      dataPoint[point.name] = point[metric].V * (multiplier + 0.2);
    });
    
    return dataPoint;
  });
};

const getTimeRangeLabel = (value: number) => {
  switch (value) {
    case 0:
      return 'Hours';
    case 1:
      return 'Days';
    case 2:
      return 'Weeks';
    case 3:
      return 'Months';
    default:
      return 'Hours';
  }
};

// Get battery color based on voltage
const getBatteryColor = (voltage: number) => {
  if (voltage >= 2.8) return '#10B981'; // green
  if (voltage >= 2.5) return '#F59E0B'; // yellow
  if (voltage >= 2.2) return '#F97316'; // orange
  return '#EF4444'; // red
};

// Calculate expected last transmission day based on battery voltage
// Based on typical discharge rate: ~0.01V per day
const getExpectedLastTransmissionDate = (voltage: number): Date | null => {
  // Only calculate for orange and red levels (below 2.5V)
  if (voltage >= 2.5) return null;
  
  // Battery is considered dead at 2.0V
  const minVoltage = 2.0;
  const remainingVoltage = voltage - minVoltage;
  
  // Estimate discharge rate: ~0.01V per day (can be adjusted based on actual sensor specs)
  const dischargeRatePerDay = 0.01;
  const daysRemaining = Math.max(0, remainingVoltage / dischargeRatePerDay);
  
  const lastTransmissionDate = new Date();
  lastTransmissionDate.setDate(lastTransmissionDate.getDate() + Math.floor(daysRemaining));
  
  return lastTransmissionDate;
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'low':
      return '#10B981'; // green
    case 'medium':
      return '#F59E0B'; // yellow
    case 'high':
      return '#EF4444'; // red
    default:
      return '#6B7280'; // gray
  }
};

const getStatusBgColor = (status: string) => {
  switch (status) {
    case 'low':
      return 'bg-green-50 border-green-200';
    case 'medium':
      return 'bg-yellow-50 border-yellow-200';
    case 'high':
      return 'bg-red-50 border-red-200';
    default:
      return 'bg-gray-50 border-gray-200';
  }
};

const getStatusTextColor = (status: string) => {
  switch (status) {
    case 'low':
      return 'text-green-800';
    case 'medium':
      return 'text-yellow-800';
    case 'high':
      return 'text-red-800';
    default:
      return 'text-gray-800';
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'low':
      return 'Normal: Vibration within acceptable range';
    case 'medium':
      return 'Warning: Vibration levels elevated';
    case 'high':
      return 'Critical: Vibration levels require immediate attention';
    default:
      return 'Unknown status';
  }
};

// Get distinct color for each point for chart differentiation
const POINT_COLORS = [
  '#3B82F6', // blue
  '#F59E0B', // amber
  '#14B8A6', // teal
  '#EC4899', // pink
  '#8B5CF6', // purple
  '#10B981', // green
  '#EF4444', // red
  '#F97316', // orange
];

const _pointColorCache = new Map<string, string>();
const getPointColor = (pointName: string): string => {
  if (!_pointColorCache.has(pointName)) {
    _pointColorCache.set(pointName, POINT_COLORS[_pointColorCache.size % POINT_COLORS.length]);
  }
  return _pointColorCache.get(pointName)!;
};

// Generate fault history from mock reports (in real app, this would come from database)
const generateFaultHistory = (machineName: string) => {
  // Mock reports data - would be filtered by machine from database
  const mockReportsForMachine = [
    { analysis: 'Bearing', date: '2024-03-15' },
    { analysis: 'Lubrication', date: '2024-02-28' },
    { analysis: 'Lubrication', date: '2024-02-14' },
    { analysis: 'Mis-Alignment', date: '2024-01-20' },
    { analysis: 'Unbalance', date: '2024-01-10' },
    { analysis: 'Bearing', date: '2023-12-05' },
    { analysis: 'Mechanical Looseness', date: '2023-11-18' },
  ];

  // Count occurrences of each analysis type
  const counts = mockReportsForMachine.reduce((acc, report) => {
    acc[report.analysis] = (acc[report.analysis] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Convert to array and sort by count descending
  return Object.entries(counts)
    .map(([analysis, count]) => ({ analysis, count }))
    .sort((a, b) => b.count - a.count);
};

// Get chart line color - status color if alarm violated, otherwise unique point color
const getChartLineColor = (point: Point) => {
  if (point.status === 'medium' || point.status === 'high') {
    return getStatusColor(point.status);
  }
  return getPointColor(point.name);
};

// Chart Container with Copy functionality
function ChartContainer({ children, title, onExpand }: { children: React.ReactNode; title: string; onExpand?: () => void }) {
  const chartRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  const handleCopyChart = async () => {
    if (!chartRef.current) return;

    try {
      // Find the SVG element within the chart
      const svgElement = chartRef.current.querySelector('svg');
      if (!svgElement) return;

      // Get SVG dimensions
      const rect = svgElement.getBoundingClientRect();
      
      // Create a canvas
      const canvas = document.createElement('canvas');
      canvas.width = rect.width * 2; // 2x for better quality
      canvas.height = rect.height * 2;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Scale for better quality
      ctx.scale(2, 2);

      // Fill white background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, rect.width, rect.height);

      // Convert SVG to data URL
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);

      // Load image and draw to canvas
      const img = new Image();
      img.onload = async () => {
        ctx.drawImage(img, 0, 0, rect.width, rect.height);
        URL.revokeObjectURL(url);

        // Convert canvas to blob
        canvas.toBlob(async (blob) => {
          if (!blob) return;
          
          try {
            await navigator.clipboard.write([
              new ClipboardItem({ 'image/png': blob })
            ]);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          } catch (err) {
            // Clipboard API may be blocked - silently fail
          }
        });
      };
      img.src = url;
    } catch (err) {
      // Error copying chart - silently fail
    }
  };

  return (
    <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-gray-900">{title}</h3>
        <div className="flex items-center gap-2">
          {onExpand && (
            <button
              onClick={onExpand}
              className="p-2 hover:bg-gray-100 rounded-lg transition-all group"
              title="Expand chart"
            >
              <Maximize2 className="w-4 h-4 text-gray-600 group-hover:text-gray-900" />
            </button>
          )}
          <button
            onClick={handleCopyChart}
            className="p-2 hover:bg-gray-100 rounded-lg transition-all group"
            title="Copy chart to clipboard"
          >
            {copied ? (
              <Check className="w-4 h-4 text-green-600" />
            ) : (
              <Copy className="w-4 h-4 text-gray-600 group-hover:text-gray-900" />
            )}
          </button>
        </div>
      </div>
      <div ref={chartRef}>
        {children}
      </div>
    </div>
  );
}

// Extract unique point-name keys from ALL trend data entries (not just the first)
function pointNamesFromTrend(data: any[]): string[] {
  if (!data.length) return [];
  const keys = new Set<string>();
  for (const entry of data) {
    Object.keys(entry).forEach(k => { if (k !== 'time') keys.add(k); });
  }
  return Array.from(keys).sort();
}

const MM_TO_IN = 1 / 25.4; // mm/s → in/s conversion factor

const fmtVelLabel = (s: string) => s.replace(/mm\/s/g, 'in/s');

// Build Map<bucket, Map<pointName, {file_id, measured_at}>> from raw trend rows.
// Keeps the LAST reason=2 entry per (bucket, pointName) so the dot is stable.
function buildWaveformMarkers(rows: any[], days: number): Map<string, Map<string, {file_id: number; measured_at: string}>> {
  const hourly = days <= 1;
  const weekly = days > 30;
  const result = new Map<string, Map<string, {file_id: number; measured_at: string}>>();
  for (const r of rows) {
    if (!r.file_id || r.reason !== 2) continue;
    const dt = r.measured_at || '';
    let bucket: string;
    if (hourly) {
      bucket = dt.substring(0, 13).replace(/\//g, '-');
    } else if (weekly) {
      const d = new Date(dt.substring(0, 10).replace(/\//g, '-'));
      const day = d.getDay();
      d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
      bucket = d.toISOString().substring(0, 10);
    } else {
      bucket = dt.substring(0, 10).replace(/\//g, '-');
    }
    if (!result.has(bucket)) result.set(bucket, new Map());
    result.get(bucket)!.set(r.point_name, { file_id: r.file_id, measured_at: r.measured_at });
  }
  return result;
}

// Convert flat trend API rows → recharts-ready [{time, [pointName]: avg}]
// Bucketing: hourly for 1 day, daily for ≤30 days, weekly for >30 days
function buildTrendData(rows: any[], days: number = 30): any[] {
  const hourly = days <= 1;
  const weekly = days > 30;
  const buckets = new Map<string, Map<string, number[]>>();
  for (const r of rows) {
    const dt = (r.measured_at || '');
    let bucket: string;
    if (hourly) {
      // '2026/03/25 20:09:30' → '2026-03-25 20'
      bucket = dt.substring(0, 13).replace(/\//g, '-');
    } else if (weekly) {
      // Group by week: use the date of the Monday of that week
      const d = new Date(dt.substring(0, 10).replace(/\//g, '-'));
      const day = d.getDay();
      const diff = (day === 0 ? -6 : 1 - day);
      d.setDate(d.getDate() + diff);
      bucket = d.toISOString().substring(0, 10);
    } else {
      // Daily: '2026/03/25 20:09:30' → '2026-03-25'
      bucket = dt.substring(0, 10).replace(/\//g, '-');
    }
    if (!buckets.has(bucket)) buckets.set(bucket, new Map());
    const pts = buckets.get(bucket)!;
    if (r.value !== null && r.value !== undefined) {
      const vals = pts.get(r.point_name) || [];
      vals.push(r.value);
      pts.set(r.point_name, vals);
    } else {
      // Preserve time slot even when value is null (creates a gap in the chart)
      if (!pts.has(r.point_name)) pts.set(r.point_name, []);
    }
  }
  return Array.from(buckets.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([bucket, pts]) => {
      const label = hourly ? bucket.substring(11) + ':00' : bucket;
      const entry: any = { time: label };
      pts.forEach((vals, name) => {
        entry[name] = vals.length > 0
          ? parseFloat(Math.max(...vals).toFixed(4))
          : null;
      });
      return entry;
    });
}

// Convert unit_code=20 trend rows → recharts-ready [{time, Temperature: °F}]
// Rows format: { measured_at: 'YYYY/MM/DD HH:MM:SS', point_name: string, value: °C }
// Finds the point with the highest most-recent temperature, shows that point's full trend.
function buildSensorTempData(rows: any[], days: number): any[] {
  const toF = (c: number) => parseFloat((c * 9 / 5 + 32).toFixed(1));
  const hourly = days <= 1;
  const weekly = days > 30;

  // Step 1: find the point with the highest most-recent reading
  const latestByPoint = new Map<string, { temp: number; measured_at: string }>();
  for (const r of rows) {
    if (r.value == null || !r.point_name) continue;
    const existing = latestByPoint.get(r.point_name);
    if (!existing || r.measured_at > existing.measured_at) {
      latestByPoint.set(r.point_name, { temp: r.value, measured_at: r.measured_at });
    }
  }
  let hottestPoint = '';
  let hottestTemp = -Infinity;
  latestByPoint.forEach(({ temp }, name) => {
    if (temp > hottestTemp) { hottestTemp = temp; hottestPoint = name; }
  });
  if (!hottestPoint) return [];

  // Step 2: bucket only that point's readings (measured_at: 'YYYY/MM/DD HH:MM:SS')
  const buckets = new Map<string, number[]>();
  for (const r of rows) {
    if (r.value == null || r.point_name !== hottestPoint) continue;
    const dt: string = r.measured_at || '';
    const [datePart, timePart = '00:00:00'] = dt.split(' ');
    const [y, mo, d] = datePart.split('/').map(Number);
    const [h] = timePart.split(':').map(Number);
    let bucket: string;
    if (hourly) {
      bucket = `${y}-${String(mo).padStart(2,'0')}-${String(d).padStart(2,'0')} ${String(h).padStart(2,'0')}`;
    } else if (weekly) {
      const date = new Date(y, mo - 1, d);
      const day = date.getDay();
      date.setDate(date.getDate() + (day === 0 ? -6 : 1 - day));
      bucket = date.toISOString().substring(0, 10);
    } else {
      bucket = `${y}-${String(mo).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
    }
    const vals = buckets.get(bucket) || [];
    vals.push(r.value);
    buckets.set(bucket, vals);
  }
  return Array.from(buckets.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([bucket, vals]) => {
      const label = hourly ? bucket.substring(11) + ':00' : bucket;
      return { time: label, Temperature: toF(Math.max(...vals)) };
    });
}

// Y-axis upper bound: at least 20% above the moderate threshold line (or data max)
function calcYMax(data: any[], threshMod?: number | null): number {
  const vals = data.flatMap(d =>
    Object.entries(d)
      .filter(([k]) => k !== 'time')
      .map(([, v]) => v as number)
      .filter(v => v != null && !isNaN(v))
  );
  const dataMax = vals.length > 0 ? Math.max(...vals) : 0;
  const top = Math.max(dataMax, threshMod ?? 0);
  return top > 0 ? parseFloat((top * 1.2).toFixed(4)) : 1;
}

// Find nearest time bucket that has waveform data, searching outward from activeIdx
function findNearestWf(
  data: any[],
  activeIdx: number,
  wfMap: Map<string, Map<string, { file_id: number; measured_at: string }>> | undefined,
  maxSearch = 10
): { time: string; measured_at: string; point_name: string } | null {
  if (!wfMap) return null;
  for (let d = 0; d <= maxSearch; d++) {
    for (const off of d === 0 ? [0] : [d, -d]) {
      const i = activeIdx + off;
      if (i < 0 || i >= data.length) continue;
      const time = data[i]?.time;
      if (!time) continue;
      const m = wfMap.get(time);
      if (m?.size) {
        const [pName, wf] = Array.from(m.entries())[0];
        return { time, measured_at: wf.measured_at, point_name: pName };
      }
    }
  }
  return null;
}

function fmtWfLabel(measured_at: string): string {
  try {
    const [dp, tp] = measured_at.split(' ');
    const [, mo, dy] = dp.split('/');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    let h = parseInt(tp.split(':')[0]);
    const mn = tp.split(':')[1];
    const ap = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return `${months[parseInt(mo)-1]} ${parseInt(dy)} ${h}:${mn} ${ap} — click to load`;
  } catch { return 'click to load'; }
}

// Y-axis upper bound considering only visible (non-hidden) series
function calcYMaxVisible(data: any[], visibleNames: string[], threshMod?: number | null): number {
  const vals = data.flatMap(d => visibleNames.map(k => d[k] as number).filter(v => v != null && !isNaN(v)));
  const dataMax = vals.length > 0 ? Math.max(...vals) : 0;
  const top = Math.max(dataMax, threshMod ?? 0);
  return top > 0 ? parseFloat((top * 1.2).toFixed(4)) : 1;
}

// Clickable series pills — click to hide/show a series
function SeriesToggle({ names, hidden, onToggle }: { names: string[]; hidden: Set<string>; onToggle: (n: string) => void }) {
  return (
    <div className="flex flex-wrap justify-center gap-1.5 pt-1 pb-0.5">
      {names.map(name => {
        const color = getPointColor(name);
        const isHidden = hidden.has(name);
        return (
          <button key={name} onClick={() => onToggle(name)}
            className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border transition-all select-none"
            style={{ borderColor: color, background: isHidden ? '#f8fafc' : 'white', opacity: isHidden ? 0.45 : 1 }}
          >
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: isHidden ? 'transparent' : color, border: `1.5px solid ${color}` }} />
            <span style={{ color: isHidden ? '#9ca3af' : color }}>{name}</span>
          </button>
        );
      })}
    </div>
  );
}

// Apply a floor filter: data points below the floor are set to null (hidden from chart)
function applyFloor(data: any[], floor: number): any[] {
  if (floor <= 0) return data;
  return data.map(entry => {
    const out: any = { time: entry.time };
    for (const [k, v] of Object.entries(entry)) {
      if (k === 'time') continue;
      out[k] = typeof v === 'number' && v < floor ? null : v;
    }
    return out;
  });
}

// Vertical floor slider rendered to the left of a chart
function FloorSlider({ floor, setFloor, yMax }: { floor: number; setFloor: (v: number) => void; yMax: number }) {
  const step = yMax > 0 ? yMax / 200 : 0.001;
  return (
    <div className="flex flex-col items-center justify-between shrink-0" style={{ height: 300, width: 28, paddingTop: 6, paddingBottom: 4 }}>
      <span
        className="font-mono tabular-nums text-center"
        style={{ fontSize: 9, color: '#64748b', minHeight: 12, lineHeight: '12px', writingMode: 'horizontal-tb' }}
      >
        {floor > 0 ? floor.toFixed(3) : ''}
      </span>
      <input
        type="range"
        min={0}
        max={yMax || 1}
        step={step}
        value={Math.min(floor, yMax || 1)}
        onChange={e => setFloor(Number(e.target.value))}
        style={{
          writingMode: 'vertical-lr',
          direction: 'rtl',
          height: 240,
          width: 18,
          cursor: 'pointer',
          accentColor: '#64748b',
          flexShrink: 0,
        }}
        title="Drag to filter: hides data below this value"
      />
      <span style={{ fontSize: 9, color: '#9ca3af' }}>0</span>
    </div>
  );
}

// Inline sparkline — renders a small SVG polyline from an array of values
function Sparkline({ values, color = '#6B7280' }: { values: number[]; color?: string }) {
  if (values.length < 2) return <span className="inline-block w-[60px]" />;
  const w = 60, h = 20, pad = 2;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const pts = values
    .map((v, i) => {
      const x = pad + (i / (values.length - 1)) * (w - 2 * pad);
      const y = pad + (1 - (v - min) / range) * (h - 2 * pad);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg width={w} height={h} style={{ overflow: 'visible', flexShrink: 0 }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

// Extract ordered values for one point from a trendDataMap entry array
function getSparklineValues(trendMap: any[], pointName: string): number[] {
  return trendMap
    .map(entry => entry[pointName])
    .filter((v): v is number => typeof v === 'number' && !isNaN(v));
}

// Pick sparkline color based on % change magnitude
function sparklineColor(pct: number): string {
  if (Math.abs(pct) < 1) return '#9CA3AF';
  return pct > 0 ? '#EF4444' : '#10B981';
}

export function MachineDetail({ machineId, machineName, points, initialPointId, initialPointName, initialAxisNum, onBack, onImageUpload }: MachineDetailProps) {
  const [trendDays, setTrendDays] = useState(180);
  const [selectedPoint, setSelectedPoint] = useState<Point | null>(null);
  const [expandedChart, setExpandedChart] = useState<{ title: string; chart: React.ReactNode } | null>(null);
  const [copied, setCopied] = useState(false);
  const copyRef = useRef<HTMLDivElement>(null);
  const [alarmHistory, setAlarmHistory] = useState<AlarmEvent[]>([]);
  const [rateOfChange, setRateOfChange] = useState<RateOfChange[]>([]);
  const [rocCachePending, setRocCachePending] = useState(false);
  const [faultHistory, setFaultHistory] = useState<{ analysis: string; count: number }[]>([]);
  const [machineLoading, setMachineLoading] = useState(true);
  // trend data keyed by unit_code, each entry is chart-ready {time, [pointName]: value}[]
  const [trendDataMap, setTrendDataMap] = useState<Record<number, any[]>>({ 0: [], 2: [], 6: [], 20: [] });
  // RPM
  const [rpmData, setRpmData] = useState<any | null>(null);
  const [rpmEditing, setRpmEditing] = useState(false);
  const [rpmForm, setRpmForm] = useState({ operating_rpm: '', rpm_min: '', rpm_max: '', is_vfd: false, rpm_notes: '' });
  const [rpmSaving, setRpmSaving] = useState(false);
  const [rpmError, setRpmError] = useState('');
  // waveform markers: per unit_code → Map<bucket, Map<pointName, {file_id, measured_at}>>
  const [waveformMarkersMap, setWaveformMarkersMap] = useState<Record<number, Map<string, Map<string, {file_id: number; measured_at: string}>>>>({});
  const [waveformJumpTo, setWaveformJumpTo] = useState<WaveformJumpTo | null>(null);
  const [waveformAutoLoad, setWaveformAutoLoad] = useState(false);
  const [wfHover, setWfHover] = useState<{ unitCode: number; time: string; measured_at: string; point_name: string } | null>(null);
  const [isAlarmHistoryExpanded, setIsAlarmHistoryExpanded] = useState(false);
  const [isRateOfChangeExpanded, setIsRateOfChangeExpanded] = useState(false);
  const [editingSensor, setEditingSensor] = useState<Point | null>(null);
  const [machineAxes, setMachineAxes] = useState<any[]>([]);
  const [pointAlarmSeverity, setPointAlarmSeverity] = useState<Map<string, 'moderate' | 'severe'>>(new Map());
  const [sensors, setSensors] = useState<any[]>([]);
  const [sensorHealth, setSensorHealth] = useState<Record<string, any[]>>({});
  const [sensorTempData, setSensorTempData] = useState<any[]>([]);

  const [healthScore, setHealthScore] = useState<MachineHealthScore | null>(null);
  const [healthAiBriefing, setHealthAiBriefing] = useState<string | null>(null);
  const [healthAiLoading, setHealthAiLoading] = useState(false);

  // Per-chart floor filter: values below this are hidden (set to null)
  const [velFloor,   setVelFloor]   = useState(0);
  const [accelFloor, setAccelFloor] = useState(0);
  const [envFloor,   setEnvFloor]   = useState(0);

  const [pvTrend, setPvTrend]         = useState<any | null>(null);
  const [pvTrendLoading, setPvTrendLoading] = useState(false);
  const [tempFloor,  setTempFloor]  = useState(0);
  const [primaryUnit, setPrimaryUnit] = useState<number>(0);
  const [additionalUnits, setAdditionalUnits] = useState<number[]>([]);
  const [showTempOverlay, setShowTempOverlay] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);

  // Per-chart hidden series: click legend pill to toggle
  const [hiddenSeries, setHiddenSeries] = useState<Record<number, Set<string>>>({ 0: new Set(), 2: new Set(), 6: new Set(), 20: new Set() });
  const toggleSeries = (uc: number, name: string) => setHiddenSeries(prev => {
    const s = new Set(prev[uc]);
    if (s.has(name)) s.delete(name); else s.add(name);
    return { ...prev, [uc]: s };
  });

  // Derive chart alarm threshold lines from already-loaded machine axes data
  const chartThresholds = useMemo(() => {
    const result: Record<number, { moderate: number; severe: number } | null> = { 0: null, 2: null };
    if (!machineAxes.length) return result;
    let velMod = 0, velSev = 0, accelMod = 0, accelSev = 0;
    let hasVel = false, hasAccel = false;
    for (const ax of machineAxes) {
      if (ax.vel_threshold != null && ax.vel_threshold > 0) {
        const mod = ax.vel_threshold * MM_TO_IN;
        const sev = (ax.vel_threshold_high ?? ax.vel_threshold * 1.5) * MM_TO_IN;
        if (!hasVel || mod > velMod) { velMod = mod; velSev = sev; }
        hasVel = true;
      }
      if (ax.accel_threshold != null && ax.accel_threshold > 0) {
        const mod = ax.accel_threshold;
        const sev = ax.accel_threshold_high ?? ax.accel_threshold * 1.5;
        if (!hasAccel || mod > accelMod) { accelMod = mod; accelSev = sev; }
        hasAccel = true;
      }
    }
    if (hasVel)   result[2] = { moderate: velMod,   severe: velSev   };
    if (hasAccel) result[0] = { moderate: accelMod, severe: accelSev };
    return result;
  }, [machineAxes]);

  // Auto-open PointDetail when navigating from sidebar point/axis click
  const autoOpenedByIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!initialPointId || machineAxes.length === 0) return;
    if (autoOpenedByIdRef.current === initialPointId) return;
    const matchingAxes = machineAxes.filter((ax: any) => ax.point_name === initialPointId);
    if (matchingAxes.length === 0) {
      const point = points.find(p => p.id === initialPointId);
      if (point) setSelectedPoint(point);
      return;
    }
    autoOpenedByIdRef.current = initialPointId;
    const vel:   { H: number; V: number; A: number } = { H: 0, V: 0, A: 0 };
    const accel: { H: number; V: number; A: number } = { H: 0, V: 0, A: 0 };
    const env:   { H: number; V: number; A: number } = { H: 0, V: 0, A: 0 };
    const temp:  { H: number; V: number; A: number } = { H: 0, V: 0, A: 0 };
    let velThr = 0, velThrHigh = 0, accelThr = 0, accelThrHigh = 0, envThr = 0, envThrHigh = 0;
    let inAlarm = false;
    const axisIds: number[] = [];
    for (const ax of matchingAxes) {
      const k = ax.axis_num === 1 ? 'H' : ax.axis_num === 2 ? 'V' : 'A';
      if (ax.latest_vel   != null) vel[k]   = ax.latest_vel * MM_TO_IN;
      if (ax.latest_accel != null) accel[k] = ax.latest_accel;
      if (ax.latest_env   != null) env[k]   = ax.latest_env;
      if (ax.latest_temp  != null) temp[k]  = ax.latest_temp;
      if (ax.vel_threshold        != null) velThr       = Math.max(velThr,       ax.vel_threshold * MM_TO_IN);
      if (ax.vel_threshold_high   != null) velThrHigh   = Math.max(velThrHigh,   ax.vel_threshold_high * MM_TO_IN);
      if (ax.accel_threshold      != null) accelThr     = Math.max(accelThr,     ax.accel_threshold);
      if (ax.accel_threshold_high != null) accelThrHigh = Math.max(accelThrHigh, ax.accel_threshold_high);
      if (ax.env_threshold        != null) envThr       = Math.max(envThr,       ax.env_threshold);
      if (ax.env_threshold_high   != null) envThrHigh   = Math.max(envThrHigh,   ax.env_threshold_high);
      if (ax.in_alarm) inAlarm = true;
      if (ax.id != null) axisIds.push(ax.id);
    }
    setSelectedPoint({
      id: initialPointId,
      name: initialPointId,
      velocity: vel, acceleration: accel, envelope: env, temperature: temp,
      status: inAlarm ? 'high' : 'low',
      velocityThresholds:     { yellow: velThr,   red: velThrHigh   || velThr   * 1.5 },
      accelerationThresholds: { yellow: accelThr, red: accelThrHigh || accelThr * 1.5 },
      envelopeThresholds:     { yellow: envThr,   red: envThrHigh   || envThr   * 1.5 },
      temperatureThresholds:  { yellow: 0, red: 0 },
      machineId, axisIds,
    });
  }, [initialPointId, machineAxes]);

  // Auto-open PointDetail when navigating from dashboard "Visit Trend" button
  const autoOpenedPointRef = useRef<string | null>(null);
  useEffect(() => {
    if (!initialPointName || machineAxes.length === 0) return;
    if (autoOpenedPointRef.current === initialPointName) return;
    const matchingAxes = machineAxes.filter((ax: any) => ax.point_name === initialPointName);
    if (matchingAxes.length === 0) return;
    autoOpenedPointRef.current = initialPointName;
    const vel:   { H: number; V: number; A: number } = { H: 0, V: 0, A: 0 };
    const accel: { H: number; V: number; A: number } = { H: 0, V: 0, A: 0 };
    const env:   { H: number; V: number; A: number } = { H: 0, V: 0, A: 0 };
    const temp:  { H: number; V: number; A: number } = { H: 0, V: 0, A: 0 };
    let velThr = 0, velThrHigh = 0, accelThr = 0, accelThrHigh = 0, envThr = 0, envThrHigh = 0;
    let inAlarm = false;
    const axisIds: number[] = [];
    for (const ax of matchingAxes) {
      const k = ax.axis_num === 1 ? 'H' : ax.axis_num === 2 ? 'V' : 'A';
      if (ax.latest_vel   != null) vel[k]   = ax.latest_vel * MM_TO_IN;
      if (ax.latest_accel != null) accel[k] = ax.latest_accel;
      if (ax.latest_env   != null) env[k]   = ax.latest_env;
      if (ax.latest_temp  != null) temp[k]  = ax.latest_temp;
      if (ax.vel_threshold        != null) velThr      = Math.max(velThr,      ax.vel_threshold * MM_TO_IN);
      if (ax.vel_threshold_high   != null) velThrHigh  = Math.max(velThrHigh,  ax.vel_threshold_high * MM_TO_IN);
      if (ax.accel_threshold      != null) accelThr    = Math.max(accelThr,    ax.accel_threshold);
      if (ax.accel_threshold_high != null) accelThrHigh = Math.max(accelThrHigh, ax.accel_threshold_high);
      if (ax.env_threshold        != null) envThr      = Math.max(envThr,      ax.env_threshold);
      if (ax.env_threshold_high   != null) envThrHigh  = Math.max(envThrHigh,  ax.env_threshold_high);
      if (ax.in_alarm) inAlarm = true;
      if (ax.id != null) axisIds.push(ax.id);
    }
    setSelectedPoint({
      id: initialPointName,
      name: initialPointName,
      velocity: vel,
      acceleration: accel,
      envelope: env,
      temperature: temp,
      status: inAlarm ? 'high' : 'low',
      velocityThresholds:     { yellow: velThr,   red: velThrHigh   || velThr * 1.5 },
      accelerationThresholds: { yellow: accelThr, red: accelThrHigh || accelThr * 1.5 },
      envelopeThresholds:     { yellow: envThr,   red: envThrHigh   || envThr * 1.5 },
      temperatureThresholds:  { yellow: 0, red: 0 },
      machineId,
      axisIds,
    });
  }, [initialPointName, machineAxes]);

  // Fetch health score
  useEffect(() => {
    if (!machineId) return;
    apiFetch<MachineHealthScore>(`/machines/${machineId}/health-score`)
      .then(data => setHealthScore(data))
      .catch(() => {});
  }, [machineId]);

  // Fetch PeakVue trend (admin only — others get empty response)
  useEffect(() => {
    if (!machineId) return;
    setPvTrendLoading(true);
    apiFetch<any>(`/machines/${machineId}/peakvue-trend?days=${trendDays}`)
      .then(data => setPvTrend(data))
      .catch(() => setPvTrend(null))
      .finally(() => setPvTrendLoading(false));
  }, [machineId, trendDays]);

  // Fetch machine detail (alarms, rate-of-change, fault types)
  useEffect(() => {
    if (!machineId) return;
    setMachineLoading(true);
    apiFetch<any>(`/machines/${machineId}`)
      .then(data => {
        // Map recent_alarms → AlarmEvent[]
        const apiAlarms: AlarmEvent[] = (data.recent_alarms || []).map((a: any) => {
          const velScale = a.unit_code === 2 ? MM_TO_IN : 1;
          const peakVal  = (a.peak_value ?? 0) * velScale;
          const thr      = (a.threshold  ?? 0) * velScale;
          return {
            id: String(a.id),
            pointName: a.point_name || '',
            metric: fmtVelLabel(a.unit_label || ''),
            value: peakVal,
            threshold: thr,
            severity: (a.alarm_level ?? 1) >= 2 ? 'high' : 'medium',
            timestamp: a.measurement_at || '',
            description: `${fmtVelLabel(a.unit_label || '')} exceeded ${(a.alarm_level ?? 1) >= 2 ? 'critical' : 'warning'} threshold (${thr.toFixed(3)}) at ${a.point_name}`,
          };
        });
        setAlarmHistory(apiAlarms);

        // Map rate_of_change → RateOfChange[] (group by point_name)
        const unitKey: Record<number, keyof RateOfChange> = { 0: 'acceleration', 2: 'velocity', 6: 'envelope', 20: 'temperature' };
        const rocMap = new Map<string, any>();
        for (const r of (data.rate_of_change || [])) {
          const key = r.point_name;
          if (!rocMap.has(key)) rocMap.set(key, {
            pointName: key,
            velocity:     { current: 0, average: 0, change: 0, percentChange: 0 },
            acceleration: { current: 0, average: 0, change: 0, percentChange: 0 },
            envelope:     { current: 0, average: 0, change: 0, percentChange: 0 },
            temperature:  { current: 0, average: 0, change: 0, percentChange: 0 },
          });
          const metric = unitKey[r.unit_code as number];
          if (metric) {
            const scale = r.unit_code === 2 ? MM_TO_IN : 1;
            const cur = (r.recent_avg ?? 0) * scale;
            const avg = (r.prev_avg   ?? 0) * scale;
            const pct = r.change_pct ?? 0;
            // Keep the axis with the highest absolute change for this (point, unit)
            const existing = rocMap.get(key)[metric];
            if (Math.abs(pct) > Math.abs(existing.percentChange)) {
              rocMap.get(key)[metric] = {
                current:       cur,
                average:       avg,
                change:        cur - avg,
                percentChange: pct,
              };
            }
          }
        }
        setRateOfChange(Array.from(rocMap.values()));
        setRocCachePending(data.cache_pending === true);

        // RPM
        setRpmData({
          operating_rpm:   data.operating_rpm ?? null,
          rpm_min:         data.rpm_min ?? null,
          rpm_max:         data.rpm_max ?? null,
          is_vfd:          data.is_vfd ?? false,
          rpm_confidence:  data.rpm_confidence ?? null,
          rpm_source:      data.rpm_source ?? null,
          rpm_detected_at: data.rpm_detected_at ?? null,
          rpm_notes:       data.rpm_notes ?? null,
        });

        // Fault types
        setFaultHistory(data.fault_types || []);

        // Raw axes data for real-time metrics
        setMachineAxes(data.axes || []);

        // Build 24h severity map: point_name → 'moderate' | 'severe'
        const cutoff24h = Date.now() - 24 * 60 * 60 * 1000;
        const sevMap = new Map<string, 'moderate' | 'severe'>();
        for (const a of (data.recent_alarms || [])) {
          const ts = (a.measurement_at || '').replace(/\//g, '-').replace(' ', 'T');
          const dt = new Date(ts).getTime();
          if (!isNaN(dt) && dt >= cutoff24h) {
            const sev: 'moderate' | 'severe' = (a.alarm_level ?? 1) >= 2 ? 'severe' : 'moderate';
            if (sevMap.get(a.point_name) !== 'severe') sevMap.set(a.point_name, sev);
          }
        }
        setPointAlarmSeverity(sevMap);

      })
      .catch(() => { /* leave arrays empty */ })
      .finally(() => setMachineLoading(false));
  }, [machineId]);

  // Fetch trend data for each unit_code when time range sliders change
  const timeRangeToDays = (tr: number) => [1, 7, 30, 180][tr] ?? 7;

  // Velocity
  useEffect(() => {
    if (!machineId) return;
    apiFetch<any[]>(`/machines/${machineId}/trend?unit_code=2&days=${trendDays}`)
      .then(rows => {
        const scaled = rows.map(r => ({ ...r, value: r.value != null ? r.value * MM_TO_IN : null }));
        setTrendDataMap(prev => ({ ...prev, 2: buildTrendData(scaled, trendDays) }));
        setWaveformMarkersMap(prev => ({ ...prev, 2: buildWaveformMarkers(rows, trendDays) }));
      }).catch(() => {});
  }, [machineId, trendDays]);

  // Acceleration
  useEffect(() => {
    if (!machineId) return;
    apiFetch<any[]>(`/machines/${machineId}/trend?unit_code=0&days=${trendDays}`)
      .then(rows => {
        setTrendDataMap(prev => ({ ...prev, 0: buildTrendData(rows, trendDays) }));
        setWaveformMarkersMap(prev => ({ ...prev, 0: buildWaveformMarkers(rows, trendDays) }));
      }).catch(() => {});
  }, [machineId, trendDays]);

  // Envelope
  useEffect(() => {
    if (!machineId) return;
    apiFetch<any[]>(`/machines/${machineId}/trend?unit_code=6&days=${trendDays}`)
      .then(rows => {
        setTrendDataMap(prev => ({ ...prev, 6: buildTrendData(rows, trendDays) }));
        setWaveformMarkersMap(prev => ({ ...prev, 6: buildWaveformMarkers(rows, trendDays) }));
      }).catch(() => {});
  }, [machineId, trendDays]);

  // Temperature — from measurements unit_code=20 (full historical data)
  useEffect(() => {
    if (!machineId) return;
    apiFetch<any[]>(`/machines/${machineId}/trend?unit_code=20&days=${trendDays}`)
      .then(rows => {
        setSensorTempData(buildSensorTempData(rows ?? [], trendDays));
      }).catch(() => setSensorTempData([]));
  }, [machineId, trendDays]);


  useEffect(() => {
    if (!machineId) return;
    apiFetch<any[]>(`/machines/${machineId}/sensors`)
      .then(data => setSensors(data ?? []))
      .catch(() => setSensors([]));
    apiFetch<any[]>(`/machines/${machineId}/sensor-health?days=30`)
      .then(data => {
        const grouped: Record<string, any[]> = {};
        for (const row of data ?? []) {
          if (!grouped[row.phantom_code]) grouped[row.phantom_code] = [];
          grouped[row.phantom_code].push(row);
        }
        setSensorHealth(grouped);
      })
      .catch(() => setSensorHealth({}));
  }, [machineId]);

  // Auto-load latest TWF on mount
  useEffect(() => {
    apiFetch<any[]>(`/measurements/twf-available?machine_id=${machineId}&limit=1`)
      .then(data => {
        if (data && data.length > 0) {
          setWaveformJumpTo({ measured_at: data[0].measured_at, point_name: data[0].point_name });
          setWaveformAutoLoad(true);
        }
      })
      .catch(() => {});
  }, [machineId]);

  const handleCopy = async () => {
    if (copyRef.current) {
      try {
        await navigator.clipboard.writeText(copyRef.current.innerText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        // Clipboard API may be blocked - silently fail
      }
    }
  };

  // Format ROC avg values — use enough decimal places to show non-zero for small readings
  const fmtRoc = (v: number, isTemp = false): string => {
    if (isTemp) return v.toFixed(0);
    if (v === 0) return '0.00';
    if (Math.abs(v) < 0.01) return v.toFixed(4);
    if (Math.abs(v) < 0.1)  return v.toFixed(3);
    return v.toFixed(2);
  };

  // Get highest rate of change for each point (used for sorting)
  const getHighestRateOfChange = (roc: RateOfChange) => {
    return Math.max(
      Math.abs(roc.velocity.percentChange),
      Math.abs(roc.acceleration.percentChange),
      Math.abs(roc.envelope.percentChange),
      Math.abs(roc.temperature.percentChange)
    );
  };

  // Sort rate of change by highest percentage change
  const sortedRateOfChange = [...rateOfChange].sort((a, b) => {
    return getHighestRateOfChange(b) - getHighestRateOfChange(a);
  });

  // Get top 3 for collapsed view
  const top3RateOfChange = sortedRateOfChange.slice(0, 3);

  // Flatten all metrics across all points for collapsed view
  const flattenedMetrics = rateOfChange.flatMap(roc => [
    { pointName: roc.pointName, metric: 'Velocity', data: roc.velocity },
    { pointName: roc.pointName, metric: 'Acceleration', data: roc.acceleration },
    { pointName: roc.pointName, metric: 'Envelope', data: roc.envelope },
    { pointName: roc.pointName, metric: 'Temperature', data: roc.temperature },
  ]);

  // Sort by absolute percentage change and get top 3
  const top3Metrics = flattenedMetrics
    .sort((a, b) => Math.abs(b.data.percentChange) - Math.abs(a.data.percentChange))
    .slice(0, 3);

  // Group machineAxes by point_name to produce live metric summaries.
  // Amplitude values come from liveAxesData (windowed trend) when available,
  // falling back to latest_* from machineAxes for the initial render.
  const livePointsMap = new Map<string, {
    name: string;
    vel:   { H: number; V: number; A: number };
    accel: { H: number; V: number; A: number };
    env:   { H: number; V: number; A: number };
    temp:  { H: number; V: number; A: number };
    velThreshold: number;
    accelThreshold: number;
    accelThresholdHigh?: number;
    envThreshold: number;
    envThresholdHigh?: number;
    velThresholdHigh?: number;
    axisIds: number[];
    inAlarm: boolean;
  }>();

  // Seed from machineAxes (thresholds + fallback amplitudes + axisIds)
  for (const ax of machineAxes) {
    const key = ax.point_name;
    if (!livePointsMap.has(key)) {
      livePointsMap.set(key, {
        name: key,
        vel:   { H: 0, V: 0, A: 0 },
        accel: { H: 0, V: 0, A: 0 },
        env:   { H: 0, V: 0, A: 0 },
        temp:  { H: 0, V: 0, A: 0 },
        velThreshold: 0,
        accelThreshold: 0,
        envThreshold: 0,
        axisIds: [],
        inAlarm: false,
      });
    }
    const pt = livePointsMap.get(key)!;
    if (ax.id != null) pt.axisIds.push(ax.id);
    const axisKey = ax.axis_num === 1 ? 'H' : ax.axis_num === 2 ? 'V' : 'A';
    // Always seed latest_* as baseline; liveAxesData overrides below for vel/accel/env
    // Temperature is never in liveAxesData so latest_temp is always authoritative
    if (ax.latest_vel   != null) pt.vel[axisKey]   = ax.latest_vel * MM_TO_IN;
    if (ax.latest_accel != null) pt.accel[axisKey] = ax.latest_accel;
    if (ax.latest_env   != null) pt.env[axisKey]   = ax.latest_env;
    if (ax.latest_temp  != null) pt.temp[axisKey]  = ax.latest_temp;
    if (ax.in_alarm) pt.inAlarm = true;
    if (ax.vel_threshold        != null) pt.velThreshold        = Math.max(pt.velThreshold,        ax.vel_threshold * MM_TO_IN);
    if (ax.vel_threshold_high   != null) pt.velThresholdHigh    = Math.max(pt.velThresholdHigh   ?? 0, ax.vel_threshold_high * MM_TO_IN);
    if (ax.accel_threshold      != null) pt.accelThreshold      = Math.max(pt.accelThreshold,     ax.accel_threshold);
    if (ax.accel_threshold_high != null) pt.accelThresholdHigh  = Math.max(pt.accelThresholdHigh ?? 0, ax.accel_threshold_high);
    if (ax.env_threshold        != null) pt.envThreshold        = Math.max(pt.envThreshold,       ax.env_threshold);
    if (ax.env_threshold_high   != null) pt.envThresholdHigh    = Math.max(pt.envThresholdHigh   ?? 0, ax.env_threshold_high);
  }


  const livePoints = Array.from(livePointsMap.values());

  // Sensor board temperature keyed by point_name — used when unit_code=20 measurements are absent
  const sensorTempByPoint = new Map<string, number>();
  for (const s of sensors) {
    if (s.sensor_temperature != null && s.point_name) {
      const existing = sensorTempByPoint.get(s.point_name);
      // Keep the reading from the most recently active sensor for this point
      if (existing == null) {
        sensorTempByPoint.set(s.point_name, s.sensor_temperature);
      }
    }
  }

  // Point names derived independently per metric so each chart only shows its own series
  const velPointNames  = pointNamesFromTrend(trendDataMap[2]);
  const accelPointNames = pointNamesFromTrend(trendDataMap[0]);
  const envPointNames  = pointNamesFromTrend(trendDataMap[6]);
  const tempPointNames = pointNamesFromTrend(sensorTempData);

  // Memoize filtered arrays so wfHover state changes don't create new array references
  // and cause Recharts to re-mount charts on every mouse move.
  const filteredVel   = useMemo(() => applyFloor(trendDataMap[2],   velFloor),   [trendDataMap[2],   velFloor]);
  const filteredAccel = useMemo(() => applyFloor(trendDataMap[0], accelFloor), [trendDataMap[0], accelFloor]);
  const filteredEnv   = useMemo(() => applyFloor(trendDataMap[6],   envFloor),   [trendDataMap[6],   envFloor]);
  const filteredTemp  = useMemo(() => applyFloor(sensorTempData,   tempFloor),  [sensorTempData,   tempFloor]);

  // Ref to capture latest wfHover without stale-closure issues in onClick
  const wfHoverRef = useRef<{ unitCode: number; time: string; measured_at: string; point_name: string } | null>(null);
  wfHoverRef.current = wfHover;

  // Ref for PeakVue chart hover — tracks (measured_at, point_name) under cursor
  const pvHoverRef = useRef<{ t: string; pname: string } | null>(null);

  // Render PointDetail as a full page when a point is selected
  if (selectedPoint) {
    return (
      <PointDetail point={selectedPoint} onBack={() => setSelectedPoint(null)} initialAxisNum={initialAxisNum} />
    );
  }

  return (
    <div className="space-y-6">
      {/* Sensor Edit Modal */}
      {editingSensor && (
        <SensorEditModal
          point={editingSensor}
          onClose={() => setEditingSensor(null)}
          onDeleteSensor={(pointId) => {
            // In a real app, this would update the backend
            console.log('Delete sensor for point:', pointId);
            setEditingSensor(null);
          }}
          onUpdateSensor={(_pointId, _serialNumber, axisMapping) => {
            if (!editingSensor) return;
            apiFetch(`/machines/${machineId}/sensors/${editingSensor.phantom_code}`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ axisMapping }),
            }).finally(() => setEditingSensor(null));
          }}
        />
      )}

      {/* Header — sticky while scrolling */}
      <div className="sticky top-0 z-20 -mx-6 px-6 py-3 bg-white/95 backdrop-blur-sm border-b border-gray-100 flex items-center gap-3 shadow-sm">
        <button
          onClick={onBack}
          className="p-1.5 hover:bg-gray-100 rounded-lg transition-all shrink-0"
        >
          <ArrowLeft className="w-4 h-4 text-gray-700" />
        </button>
        <h2 className="text-gray-900 text-base font-semibold truncate">{machineName}</h2>
      </div>

      {/* Machine Health Score */}
      {healthScore && (
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-5 border border-gray-200 shadow-sm">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            {/* Score badge + label */}
            <div className="flex items-center gap-4">
              <div
                className="w-16 h-16 rounded-xl flex items-center justify-center shrink-0 shadow-sm"
                style={{ backgroundColor: healthScore.health_colour }}
              >
                <span className="text-3xl font-bold text-white leading-none">{healthScore.health_score}</span>
              </div>
              <div>
                <div className="text-lg font-semibold text-gray-900">{healthScore.health_label}</div>
                <div className="text-xs text-gray-500 mt-0.5">Machine Health Score</div>
                {healthScore.primary_driver && (
                  <div className="text-sm text-gray-600 mt-1">{healthScore.primary_driver}</div>
                )}
                {healthScore.mitigating_factor && (
                  <div className="text-sm text-green-600 mt-0.5">✓ {healthScore.mitigating_factor}</div>
                )}
              </div>
            </div>

            {/* Component bars — admin only */}
            {(() => {
              const user = JSON.parse(localStorage.getItem('pc_user') || '{}');
              if (user?.role !== 'admin') return null;
              return (
                <div className="flex flex-col gap-2 min-w-[200px]">
                  {[
                    { label: 'Alarm Status',    value: healthScore.alarm_component,          max: 30 },
                    { label: 'PeakVue Alarm',   value: healthScore.peakvue_alarm_component ?? 20, max: 20 },
                    { label: 'Spectral Health', value: healthScore.spectral_component,       max: 20 },
                    { label: 'Trend (ROC)',     value: healthScore.roc_component,            max: 15 },
                    { label: 'PeakVue Trend',   value: healthScore.peakvue_trend_component ?? 13, max: 15 },
                  ].map(c => {
                    const pct = Math.round((c.value / c.max) * 100);
                    const barColor = pct >= 80 ? '#16a34a' : pct >= 50 ? '#ca8a04' : '#dc2626';
                    return (
                      <div key={c.label}>
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-xs text-gray-500">{c.label}</span>
                          <span className="text-xs font-medium text-gray-700">{c.value}/{c.max}</span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: barColor }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          {/* AI health context button */}
          <div className="mt-4 flex items-center gap-2">
            <button
              onClick={() => {
                if (healthAiBriefing) { setHealthAiBriefing(null); return; }
                setHealthAiLoading(true);
                apiFetch<{ briefing: string }>('/ai/machine-summary', {
                  method: 'POST',
                  body: JSON.stringify({ machine_id: machineId }),
                })
                  .then(res => setHealthAiBriefing(res.briefing))
                  .catch(() => setHealthAiBriefing('Unable to load AI context.'))
                  .finally(() => setHealthAiLoading(false));
              }}
              disabled={healthAiLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
            >
              <Sparkles className="w-3.5 h-3.5" />
              {healthAiBriefing ? 'Hide AI Context' : 'AI Health Context ✦'}
            </button>
          </div>

          {/* AI briefing */}
          {healthAiLoading && (
            <div className="mt-3 space-y-1.5">
              {[1, 2, 3].map(i => <div key={i} className="h-4 bg-gray-100 rounded animate-pulse" />)}
            </div>
          )}
          {healthAiBriefing && !healthAiLoading && (
            <div className="mt-3 bg-cyan-50 rounded-lg p-3">
              <p className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">{healthAiBriefing}</p>
            </div>
          )}
        </div>
      )}

      {/* Machine Image Upload */}
      {onImageUpload && (
        <div className="flex justify-end">
          <label
            htmlFor="machine-image-upload"
            className="flex items-center gap-2 px-3 py-2 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg cursor-pointer shadow-sm transition-all text-sm text-gray-700"
            title="Upload machine image"
          >
            <Upload className="w-4 h-4" />
            Upload Image
            <input
              type="file"
              id="machine-image-upload"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    const base64String = reader.result as string;
                    onImageUpload(machineId, base64String);
                  };
                  reader.readAsDataURL(file);
                }
              }}
            />
          </label>
        </div>
      )}

      {/* Operating Speed */}
      {(() => {
        const isAdmin = JSON.parse(localStorage.getItem('pc_user') || '{}')?.role === 'admin';
        const rpm = rpmData;

        const badgeEl = (badge: string) => {
          const cls: Record<string, string> = {
            auto:   'bg-teal-50  text-teal-700  border-teal-200',
            manual: 'bg-blue-50  text-blue-700  border-blue-200',
            vfd:    'bg-amber-50 text-amber-700 border-amber-200',
            verify: 'bg-amber-50 text-amber-700 border-amber-200',
            unset:  'bg-gray-50  text-gray-400  border-gray-200',
          };
          return (
            <span className={`text-xs px-2 py-0.5 rounded-full border ${cls[badge] || cls.unset}`}>
              {badge}
            </span>
          );
        };

        const saveRpm = async () => {
          setRpmSaving(true); setRpmError('');
          const rpmVal = parseFloat(rpmForm.operating_rpm);
          if (isNaN(rpmVal) || rpmVal < 60 || rpmVal > 15000) {
            setRpmError('RPM must be a number between 60 and 15,000'); setRpmSaving(false); return;
          }
          if (rpmForm.is_vfd) {
            const mn = parseFloat(rpmForm.rpm_min), mx = parseFloat(rpmForm.rpm_max);
            if (isNaN(mn) || isNaN(mx)) { setRpmError('Min and max RPM required for VFD'); setRpmSaving(false); return; }
            if (!(mn < rpmVal && rpmVal < mx)) { setRpmError('Min < Typical < Max required'); setRpmSaving(false); return; }
          }
          try {
            const body: any = { operating_rpm: rpmVal, is_vfd: rpmForm.is_vfd, rpm_notes: rpmForm.rpm_notes || null };
            if (rpmForm.is_vfd) { body.rpm_min = parseFloat(rpmForm.rpm_min); body.rpm_max = parseFloat(rpmForm.rpm_max); }
            const updated = await apiFetch<any>(`/machines/${machineId}/rpm`, { method: 'PATCH', body: JSON.stringify(body) });
            setRpmData(updated);
            setRpmEditing(false);
          } catch (e: any) {
            setRpmError(e?.message || 'Save failed');
          } finally {
            setRpmSaving(false);
          }
        };

        const openEdit = () => {
          setRpmForm({
            operating_rpm: rpm?.operating_rpm != null ? String(rpm.operating_rpm) : '',
            rpm_min:       rpm?.rpm_min != null ? String(rpm.rpm_min) : '',
            rpm_max:       rpm?.rpm_max != null ? String(rpm.rpm_max) : '',
            is_vfd:        rpm?.is_vfd ?? false,
            rpm_notes:     rpm?.rpm_notes ?? '',
          });
          setRpmError('');
          setRpmEditing(true);
        };

        const display = (() => {
          if (!rpm || rpm.operating_rpm == null) return { label: null, sublabel: 'Speed not configured', badge: 'unset' };
          const fmt = (v: number) => Math.round(v).toLocaleString();
          const src = rpm.rpm_source;
          const vfd = rpm.is_vfd;
          const conf = rpm.rpm_confidence;
          if (src === 'manual_entry') return { label: `${fmt(rpm.operating_rpm)} RPM`, sublabel: 'Manually configured', badge: 'manual', range: null };
          if (vfd) return { label: `~${fmt(rpm.operating_rpm)} RPM`, sublabel: 'Variable speed drive', badge: 'vfd', range: rpm.rpm_min && rpm.rpm_max ? `${fmt(rpm.rpm_min)}–${fmt(rpm.rpm_max)} RPM` : null };
          if (conf === 'low' || conf === 'unreliable') return { label: `~${fmt(rpm.operating_rpm)} RPM`, sublabel: 'Low confidence estimate', badge: 'verify', range: null };
          return { label: `${fmt(rpm.operating_rpm)} RPM`, sublabel: 'Fixed speed — auto detected', badge: 'auto', range: null };
        })();

        return (
          <div className="bg-white rounded-xl px-4 py-2.5 border border-gray-200 shadow-sm">
            {!rpmEditing ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Speed</span>
                  {display.label ? (
                    <>
                      <span className="text-sm font-semibold text-gray-900">⚡ {display.label}</span>
                      {badgeEl(display.badge)}
                      <span className="text-xs text-gray-400">{display.sublabel}</span>
                      {(display as any).range && <span className="text-xs text-gray-400">· {(display as any).range}</span>}
                    </>
                  ) : (
                    <span className="text-gray-400 text-sm">Not configured</span>
                  )}
                </div>
                {isAdmin && (
                  <button onClick={openEdit} className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 px-2 py-1 rounded hover:bg-gray-100 transition-colors">
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-4 max-w-sm">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Drive type</p>
                  <div className="flex gap-4 text-sm">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" checked={!rpmForm.is_vfd} onChange={() => setRpmForm(f => ({ ...f, is_vfd: false }))} /> Fixed speed
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" checked={rpmForm.is_vfd} onChange={() => setRpmForm(f => ({ ...f, is_vfd: true }))} /> Variable (VFD)
                    </label>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Operating speed (RPM)</label>
                  <input type="number" min={60} max={15000} value={rpmForm.operating_rpm}
                    onChange={e => setRpmForm(f => ({ ...f, operating_rpm: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                    placeholder="e.g. 1750" />
                  <p className="text-xs text-gray-400 mt-0.5">Typical operating speed</p>
                </div>
                {rpmForm.is_vfd && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-1">Min speed (RPM)</label>
                      <input type="number" value={rpmForm.rpm_min}
                        onChange={e => setRpmForm(f => ({ ...f, rpm_min: e.target.value }))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                        placeholder="e.g. 900" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-1">Max speed (RPM)</label>
                      <input type="number" value={rpmForm.rpm_max}
                        onChange={e => setRpmForm(f => ({ ...f, rpm_max: e.target.value }))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                        placeholder="e.g. 1800" />
                    </div>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Notes (optional)</label>
                  <input type="text" value={rpmForm.rpm_notes}
                    onChange={e => setRpmForm(f => ({ ...f, rpm_notes: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                    placeholder="e.g. Nameplate: 1750 RPM, 4-pole" />
                </div>
                {rpmError && <p className="text-sm text-red-600">{rpmError}</p>}
                <div className="flex gap-2">
                  <button onClick={() => setRpmEditing(false)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50">Cancel</button>
                  <button onClick={saveRpm} disabled={rpmSaving} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60">
                    {rpmSaving ? 'Saving…' : 'Save speed'}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ─── Unified Primary Trend ─── */}
      {(() => {
        const isAdmin = JSON.parse(localStorage.getItem('pc_user') || '{}')?.role === 'admin';
        const hasPv = isAdmin && !!(pvTrend?.points?.length);
        const PV_PLUS_FAULT_COLOURS: Record<string, string> = { highly_periodic: '#dc2626', bearing_fault: '#dc2626', cavitation_noise: '#6b7280', lubrication: '#d97706', early_fault: '#0891b2', healthy: '#16a34a' };
        const PV_PLUS_FAULT_LABELS: Record<string, string> = { highly_periodic: 'Highly Periodic', bearing_fault: 'Highly Periodic', cavitation_noise: 'Cavitation/noise', lubrication: 'Lubrication', early_fault: 'Early fault', healthy: 'Healthy' };
        const PT_COLORS = ['#2563eb','#16a34a','#9333ea','#ea580c','#0891b2','#dc2626','#d97706'];

        const renderPvChart = (height: number) => {
          if (pvTrendLoading) return <div className="bg-gray-50 rounded animate-pulse" style={{ height }} />;
          if (!pvTrend?.points?.length) return <div className="flex items-center justify-center text-gray-400 text-sm" style={{ height }}>No PeakVue data available</div>;

          // Bucket readings exactly like buildTrendData: hourly / daily / weekly MAX per point.
          // Keeps the winning reading's metadata (fault_type, measured_at) for click-to-waveform.
          const hourly = trendDays <= 1, weekly = trendDays > 30;
          const ptNames: string[] = pvTrend.points.map((p: any) => p.point_name);
          const bucketMap = new Map<string, Record<string, any>>();
          for (const pt of pvTrend.points) {
            for (const r of pt.readings) {
              const dt: string = r.measured_at || '';
              let bucket: string;
              if (hourly) { bucket = dt.substring(0, 13).replace(/\//g, '-'); }
              else if (weekly) {
                const d = new Date(dt.substring(0, 10).replace(/\//g, '-'));
                const day = d.getDay();
                d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
                bucket = d.toISOString().substring(0, 10);
              } else { bucket = dt.substring(0, 10).replace(/\//g, '-'); }
              if (!bucketMap.has(bucket)) bucketMap.set(bucket, {});
              const prev = bucketMap.get(bucket)![pt.point_name];
              if (!prev || r.max_peak_g > prev.max_peak_g) bucketMap.get(bucket)![pt.point_name] = r;
            }
          }
          const cd = Array.from(bucketMap.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([bucket, ptData]) => {
              const time = hourly ? bucket.substring(11) + ':00' : bucket;
              const row: any = { time };
              for (const pname of ptNames) {
                const r = ptData[pname];
                if (!r) continue;
                row[pname] = r.max_peak_g;
                row[`${pname}__at`] = r.measured_at;
                if (r.max_peak_g >= 5 && r.fault_type) {
                  row[`${pname}__ft`]  = r.fault_type;
                  row[`${pname}__pp`]  = r.periodic_pct;
                  row[`${pname}__rel`] = r.plus_reliability;
                }
              }
              return row;
            });

          return (
            <>
              <ResponsiveContainer width="100%" height={height}>
                <LineChart data={cd} margin={{ top: 5, right: 24, left: 0, bottom: 5 }}
                  style={{ cursor: 'pointer' }}
                  onMouseMove={(s: any) => {
                    const ap = s?.activePayload;
                    if (s?.isTooltipActive && ap?.length > 0) {
                      const pname = ap.find((p: any) => p.name && !String(p.name).includes('__'))?.name;
                      const measuredAt = pname ? ap[0]?.payload?.[`${pname}__at`] : null;
                      if (measuredAt && pname) pvHoverRef.current = { t: measuredAt, pname };
                    } else { pvHoverRef.current = null; }
                  }}
                  onMouseLeave={() => { pvHoverRef.current = null; }}
                  onClick={() => { const h = pvHoverRef.current; if (h) setWaveformJumpTo({ measured_at: h.t, point_name: h.pname, signal: 'peakvue' }); }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="time" stroke="#9CA3AF" tick={{ fontSize: 10 }} minTickGap={40} />
                  <YAxis stroke="#9CA3AF" tick={{ fontSize: 10 }} label={{ value: 'G', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#6b7280' } }} />
                  <Tooltip content={({ active, payload, label }: any) => {
                    if (!active || !payload?.length) return null;
                    const seen = new Set<string>();
                    const deduped = payload.filter((p: any) => { const k = String(p.dataKey); if (seen.has(k)) return false; seen.add(k); return true; });
                    return (
                      <div className="bg-white border border-gray-200 rounded-lg p-2 text-xs shadow">
                        <div className="font-medium mb-1 text-gray-700">{label}</div>
                        {deduped.map((p: any, i: number) => {
                          const name = String(p.dataKey ?? '');
                          if (name.includes('__')) return null;
                          const ft  = p.payload?.[`${name}__ft`];
                          const pp  = p.payload?.[`${name}__pp`];
                          const rel = p.payload?.[`${name}__rel`];
                          const val = typeof p.value === 'number' ? p.value.toFixed(3) + ' G' : p.value;
                          const detail = ft ? `  |  ${PV_PLUS_FAULT_LABELS[ft] ?? ft}  (${pp != null ? Number(pp).toFixed(0) : '—'}% periodic)${rel === 'preliminary' ? '  ⚠' : ''}` : '';
                          return <div key={i} style={{ color: p.color }}>{name} : {val}{detail}</div>;
                        })}
                      </div>
                    );
                  }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <ReferenceLine y={5}  stroke="#ca8a04" strokeDasharray="5 4" label={{ value: 'Monitor',     position: 'insideTopRight', fontSize: 9, fill: '#ca8a04' }} />
                  <ReferenceLine y={10} stroke="#d97706" strokeDasharray="5 4" label={{ value: 'Investigate', position: 'insideTopRight', fontSize: 9, fill: '#d97706' }} />
                  <ReferenceLine y={20} stroke="#ea580c" strokeDasharray="5 4" label={{ value: 'Action',      position: 'insideTopRight', fontSize: 9, fill: '#ea580c' }} />
                  <ReferenceLine y={40} stroke="#dc2626" strokeDasharray="5 4" label={{ value: 'Urgent',      position: 'insideTopRight', fontSize: 9, fill: '#dc2626' }} />
                  {ptNames.map((pname: string, i: number) => (
                    <Fragment key={pname}>
                      <Line type="monotone" dataKey={pname} stroke={PT_COLORS[i % PT_COLORS.length]} strokeWidth={1.5} connectNulls strokeDasharray="4 4" strokeOpacity={0.4} legendType="none" isAnimationActive={false} />
                      <Line type="monotone" dataKey={pname} stroke={PT_COLORS[i % PT_COLORS.length]} strokeWidth={2} connectNulls name={pname} isAnimationActive={false}
                        dot={(props: any) => {
                          const { cx, cy, payload } = props;
                          if (cx == null || cy == null) return <></>;
                          const ft  = payload[`${pname}__ft`];
                          const rel = payload[`${pname}__rel`];
                          const pp  = payload[`${pname}__pp`];
                          const ppNum = pp != null ? Number(pp) : 0;
                          // Red dot: highly_periodic >= 75%; yellow dot: 50–74%; otherwise plain
                          const isHighlyPeriodic = ft === 'highly_periodic' || ft === 'bearing_fault';
                          if (isHighlyPeriodic && ppNum >= 75) {
                            const col = PV_PLUS_FAULT_COLOURS['highly_periodic'];
                            return <circle key={`dot-${pname}-${payload.time}`} cx={cx} cy={cy} r={rel === 'high' ? 6 : 4} fill={col} stroke="white" strokeWidth={2} style={{ cursor: 'pointer' }} />;
                          }
                          if (isHighlyPeriodic && ppNum >= 50) {
                            return <circle key={`dot-${pname}-${payload.time}`} cx={cx} cy={cy} r={rel === 'high' ? 6 : 4} fill="#eab308" stroke="white" strokeWidth={2} style={{ cursor: 'pointer' }} />;
                          }
                          if (ft && !isHighlyPeriodic) {
                            const col = PV_PLUS_FAULT_COLOURS[ft] ?? '#6b7280';
                            return <circle key={`dot-${pname}-${payload.time}`} cx={cx} cy={cy} r={rel === 'high' ? 6 : 4} fill={col} stroke="white" strokeWidth={2} style={{ cursor: 'pointer' }} />;
                          }
                          return <circle key={`dot-${pname}-${payload.time}`} cx={cx} cy={cy} r={2} fill={PT_COLORS[i % PT_COLORS.length]} fillOpacity={0.5} />;
                        }}
                      />
                    </Fragment>
                  ))}
                </LineChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                <span className="text-gray-400">Plus dots (≥5G):</span>
                {[['highly_periodic','#dc2626','Highly Periodic (≥75%)'],['#eab308','#eab308','Highly Periodic (50–74%)'],['cavitation_noise','#6b7280','Cavitation/noise'],['lubrication','#d97706','Lubrication'],['early_fault','#0891b2','Early fault'],['healthy','#16a34a','Healthy']].map(([,col,lbl]) => (
                  <span key={lbl as string} className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: col as string }} />{lbl}</span>
                ))}
              </div>
            </>
          );
        };

        const renderStdChart = (uc: number, height: number) => {
          const floor = uc === 0 ? accelFloor : uc === 2 ? velFloor : envFloor;
          const setFloor = uc === 0 ? setAccelFloor : uc === 2 ? setVelFloor : setEnvFloor;
          const filtered = uc === 0 ? filteredAccel : uc === 2 ? filteredVel : filteredEnv;
          const ptNames = uc === 0 ? accelPointNames : uc === 2 ? velPointNames : envPointNames;
          const wfMarkers = waveformMarkersMap[uc] ?? new Map();
          const threshold = (uc === 0 || uc === 2) ? (chartThresholds[uc as 0 | 2] ?? null) : null;
          const visiblePts = ptNames.filter(n => !hiddenSeries[uc].has(n));
          const yMax = calcYMaxVisible(filtered, visiblePts, threshold?.moderate);
          if (filtered.length === 0) return <div className="flex items-center justify-center text-gray-400 text-sm" style={{ height }}>No data for selected period</div>;
          return (
            <div className="flex gap-2 items-start">
              <FloorSlider floor={floor} setFloor={setFloor} yMax={yMax} />
              <div className="flex-1 min-w-0" style={{ cursor: wfHover?.unitCode === uc ? 'pointer' : 'default' }}>
                <ResponsiveContainer width="100%" height={height}>
                  <LineChart data={filtered}
                    onMouseMove={(s: any) => { if (s.isTooltipActive && s.activeTooltipIndex !== undefined) { const nw = findNearestWf(filtered, s.activeTooltipIndex, wfMarkers); setWfHover(nw ? { unitCode: uc, ...nw } : null); } else { setWfHover(null); } }}
                    onMouseLeave={() => setWfHover(null)}
                    onClick={() => { const h = wfHoverRef.current; if (h?.unitCode === uc) setWaveformJumpTo({ measured_at: h.measured_at, point_name: h.point_name }); }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="time" stroke="#9CA3AF" />
                    <YAxis stroke="#9CA3AF" domain={[0, yMax]} tickFormatter={v => v.toFixed(3)} />
                    <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px' }} />
                    {threshold && <ReferenceLine y={threshold.moderate} stroke="#f59e0b" strokeDasharray="6 3" strokeWidth={1.5} label={{ value: `Mod: ${threshold.moderate.toFixed(3)}`, position: 'insideTopRight', fill: '#f59e0b', fontSize: 10, fontWeight: 600 }} />}
                    {threshold && <ReferenceLine y={threshold.severe}   stroke="#ef4444" strokeDasharray="6 3" strokeWidth={1.5} label={{ value: `Sev: ${threshold.severe.toFixed(3)}`,   position: 'insideTopRight', fill: '#ef4444', fontSize: 10, fontWeight: 600 }} />}
                    {floor > 0 && <ReferenceLine y={floor} stroke="#64748b" strokeDasharray="4 4" strokeWidth={1} label={{ value: `Floor: ${floor.toFixed(3)}`, position: 'insideTopLeft', fill: '#64748b', fontSize: 10 }} />}
                    {wfHover?.unitCode === uc && <ReferenceLine x={wfHover.time} stroke="#06b6d4" strokeWidth={2} label={{ value: fmtWfLabel(wfHover.measured_at), position: 'insideBottomLeft', fill: '#06b6d4', fontSize: 9, fontWeight: 600 }} />}
                    {visiblePts.map(name => (
                      <Fragment key={name}>
                        <Line type="monotone" dataKey={name} stroke={getPointColor(name)} strokeWidth={1.5} dot={false} connectNulls strokeDasharray="4 4" strokeOpacity={0.4} legendType="none" isAnimationActive={false} />
                        <Line type="monotone" dataKey={name} stroke={getPointColor(name)} strokeWidth={2} connectNulls name={name} isAnimationActive={false}
                          dot={(p: any) => { const { cx, cy, payload, index } = p; if (!cx || !cy) return <circle key={index} r={0} />; const wf = wfMarkers.get(payload.time)?.get(name); if (wf) return <circle key={index} cx={cx} cy={cy} r={4} fill={getPointColor(name)} stroke="white" strokeWidth={1.5} />; return <circle key={index} cx={cx} cy={cy} r={2} fill={getPointColor(name)} fillOpacity={0.5} />; }}
                        />
                      </Fragment>
                    ))}
                  </LineChart>
                </ResponsiveContainer>
                <SeriesToggle names={ptNames} hidden={hiddenSeries[uc]} onToggle={n => toggleSeries(uc, n)} />
              </div>
            </div>
          );
        };

        const renderPrimaryWithTemp = (uc: number) => {
          const floor = uc === 0 ? accelFloor : uc === 2 ? velFloor : envFloor;
          const setFloor = uc === 0 ? setAccelFloor : uc === 2 ? setVelFloor : setEnvFloor;
          const filtered = uc === 0 ? filteredAccel : uc === 2 ? filteredVel : filteredEnv;
          const ptNames = uc === 0 ? accelPointNames : uc === 2 ? velPointNames : envPointNames;
          const wfMarkers = waveformMarkersMap[uc] ?? new Map();
          const threshold = (uc === 0 || uc === 2) ? (chartThresholds[uc as 0 | 2] ?? null) : null;
          const visiblePts = ptNames.filter(n => !hiddenSeries[uc].has(n));
          const yMax = calcYMaxVisible(filtered, visiblePts, threshold?.moderate);
          const tempByTime = Object.fromEntries(sensorTempData.map((d: any) => [d.time, d]));
          const dataWithTemp = showTempOverlay && sensorTempData.length > 0
            ? filtered.map((row: any) => { const tr = tempByTime[row.time]; if (!tr) return row; const out = { ...row }; for (const [k, v] of Object.entries(tr)) if (k !== 'time') out[`__t_${k}`] = v; return out; })
            : filtered;
          const tNames = (showTempOverlay && sensorTempData.length > 0) ? tempPointNames : [];
          const tempNums = sensorTempData.flatMap((d: any) => Object.values(d).filter((v): v is number => typeof v === 'number'));
          const tempYMax = tempNums.length > 0 ? Math.ceil(Math.max(...tempNums) * 1.1) : 200;
          const tempYMin = tempNums.length > 0 ? Math.floor(Math.min(...tempNums) * 0.9) : 0;
          if (filtered.length === 0) return <div className="flex items-center justify-center h-[220px] text-gray-400 text-sm">No data for selected period</div>;
          return (
            <div className="flex gap-2 items-start">
              <FloorSlider floor={floor} setFloor={setFloor} yMax={yMax} />
              <div className="flex-1 min-w-0" style={{ cursor: wfHover?.unitCode === uc ? 'pointer' : 'default' }}>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={dataWithTemp}
                    onMouseMove={(s: any) => { if (s.isTooltipActive && s.activeTooltipIndex !== undefined) { const nw = findNearestWf(filtered, s.activeTooltipIndex, wfMarkers); setWfHover(nw ? { unitCode: uc, ...nw } : null); } else { setWfHover(null); } }}
                    onMouseLeave={() => setWfHover(null)}
                    onClick={() => { const h = wfHoverRef.current; if (h?.unitCode === uc) setWaveformJumpTo({ measured_at: h.measured_at, point_name: h.point_name }); }}
                    margin={{ right: tNames.length > 0 ? 60 : 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="time" stroke="#9CA3AF" />
                    <YAxis yAxisId="left" stroke="#9CA3AF" domain={[0, yMax]} tickFormatter={v => v.toFixed(3)} />
                    {tNames.length > 0 && <YAxis yAxisId="right" orientation="right" stroke="#F97316" domain={[tempYMin, tempYMax]} tickFormatter={v => v.toFixed(0)} unit="°F" />}
                    <Tooltip content={({ active, payload, label }: any) => {
                      if (!active || !payload?.length) return null;
                      const seen = new Set<string>();
                      const deduped = payload.filter((p: any) => { const k = String(p.dataKey); if (seen.has(k)) return false; seen.add(k); return true; });
                      return (
                        <div className="bg-white border border-gray-200 rounded-lg p-2 text-xs shadow">
                          <div className="font-medium mb-1 text-gray-700">{label}</div>
                          {deduped.map((p: any, i: number) => {
                            const key = String(p.dataKey ?? '');
                            const isTemp = key.startsWith('__t_');
                            const dispName = isTemp ? key.replace('__t_', '') + ' (°F)' : String(p.name ?? key);
                            const val = typeof p.value === 'number' ? (isTemp ? p.value.toFixed(1) + '°F' : p.value.toFixed(4)) : p.value;
                            return <div key={i} style={{ color: p.color }}>{dispName} : {val}</div>;
                          })}
                        </div>
                      );
                    }} />
                    {threshold && <ReferenceLine yAxisId="left" y={threshold.moderate} stroke="#f59e0b" strokeDasharray="6 3" strokeWidth={1.5} label={{ value: `Mod: ${threshold.moderate.toFixed(3)}`, position: 'insideTopRight', fill: '#f59e0b', fontSize: 10, fontWeight: 600 }} />}
                    {threshold && <ReferenceLine yAxisId="left" y={threshold.severe}   stroke="#ef4444" strokeDasharray="6 3" strokeWidth={1.5} label={{ value: `Sev: ${threshold.severe.toFixed(3)}`,   position: 'insideTopRight', fill: '#ef4444', fontSize: 10, fontWeight: 600 }} />}
                    {floor > 0 && <ReferenceLine yAxisId="left" y={floor} stroke="#64748b" strokeDasharray="4 4" strokeWidth={1} label={{ value: `Floor: ${floor.toFixed(3)}`, position: 'insideTopLeft', fill: '#64748b', fontSize: 10 }} />}
                    {wfHover?.unitCode === uc && <ReferenceLine yAxisId="left" x={wfHover.time} stroke="#06b6d4" strokeWidth={2} label={{ value: fmtWfLabel(wfHover.measured_at), position: 'insideBottomLeft', fill: '#06b6d4', fontSize: 9, fontWeight: 600 }} />}
                    {visiblePts.map(name => (
                      <Fragment key={name}>
                        <Line yAxisId="left" type="monotone" dataKey={name} stroke={getPointColor(name)} strokeWidth={1.5} dot={false} connectNulls strokeDasharray="4 4" strokeOpacity={0.4} legendType="none" isAnimationActive={false} />
                        <Line yAxisId="left" type="monotone" dataKey={name} stroke={getPointColor(name)} strokeWidth={2} connectNulls name={name} isAnimationActive={false}
                          dot={(p: any) => { const { cx, cy, payload, index } = p; if (!cx || !cy) return <circle key={index} r={0} />; const wf = wfMarkers.get(payload.time)?.get(name); if (wf) return <circle key={index} cx={cx} cy={cy} r={4} fill={getPointColor(name)} stroke="white" strokeWidth={1.5} />; return <circle key={index} cx={cx} cy={cy} r={2} fill={getPointColor(name)} fillOpacity={0.5} />; }}
                        />
                      </Fragment>
                    ))}
                    {tNames.map(tname => (
                      <Line key={`__t_${tname}`} yAxisId="right" type="monotone" dataKey={`__t_${tname}`} stroke="#F97316" strokeWidth={1.5} strokeDasharray="5 3" dot={{ r: 3, fill: '#F97316' }} connectNulls={false} name={`${tname} (°F)`} isAnimationActive={false} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
                <SeriesToggle names={ptNames} hidden={hiddenSeries[uc]} onToggle={n => toggleSeries(uc, n)} />
              </div>
            </div>
          );
        };

        return (
          <>
            {/* Primary Trend Card */}
            <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-3 border border-gray-200 shadow-sm">
              {/* Controls row */}
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  {[
                    { unit: 0, label: 'Acceleration' },
                    { unit: 2, label: 'Velocity' },
                    ...(hasPv ? [{ unit: -1 as number, label: 'PeakVue' }] : []),
                  ].map(({ unit, label }) => (
                    <button key={unit} onClick={() => setPrimaryUnit(unit)}
                      className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${primaryUnit === unit ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                    >{label}</button>
                  ))}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex rounded-lg overflow-hidden border border-gray-200 text-xs">
                    {([{d:7,l:'7D'},{d:30,l:'30D'},{d:90,l:'90D'},{d:180,l:'180D'},{d:365,l:'1Y'}] as {d:number;l:string}[]).map(({d,l},i) => (
                      <button key={d} onClick={() => setTrendDays(d)}
                        className={`px-2.5 py-1.5 ${i > 0 ? 'border-l border-gray-200' : ''} ${trendDays === d ? 'bg-teal-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                      >{l}</button>
                    ))}
                  </div>
                  {sensorTempData.length > 0 && primaryUnit !== -1 && (
                    <button onClick={() => setShowTempOverlay(v => !v)}
                      className={`px-2.5 py-1.5 text-xs rounded-lg border transition-colors ${showTempOverlay ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-orange-600 border-orange-200 hover:bg-orange-50'}`}
                    >Temp</button>
                  )}
                  <div className="relative">
                    <button onClick={() => setShowAddMenu(v => !v)}
                      className="px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 flex items-center gap-1"
                    ><Plus className="w-3 h-3" />Add</button>
                    {showAddMenu && (() => {
                      const addable = [
                        { unit: 2, label: 'Velocity' },
                        { unit: 0, label: 'Acceleration' },
                        { unit: 6, label: 'Envelope' },
                        ...(hasPv ? [{ unit: -1 as number, label: 'PeakVue' }] : []),
                      ].filter(x => x.unit !== primaryUnit && !additionalUnits.includes(x.unit));
                      return (
                        <div className="absolute right-0 top-8 z-20 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden min-w-[120px]">
                          {addable.map(({ unit, label }) => (
                            <button key={unit} onClick={() => { setAdditionalUnits(prev => [...prev, unit]); setShowAddMenu(false); }}
                              className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >{label}</button>
                          ))}
                          {addable.length === 0 && <div className="px-3 py-2 text-sm text-gray-400">All trends added</div>}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
              {/* Chart body */}
              {primaryUnit === -1 ? renderPvChart(220) : renderPrimaryWithTemp(primaryUnit)}
            </div>

            {/* Additional trend panels */}
            {additionalUnits.map(uc => {
              const label = uc === 0 ? "Acceleration (G's)" : uc === 2 ? 'Velocity (in/s)' : uc === 6 ? "Envelope (G's)" : 'PeakVue Trend';
              return (
                <div key={uc} className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border border-gray-200 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-gray-900">{label}</h3>
                    <button onClick={() => setAdditionalUnits(prev => prev.filter(u => u !== uc))} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                      <X className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                  {uc === -1 ? renderPvChart(240) : renderStdChart(uc, 240)}
                </div>
              );
            })}
          </>
        );
      })()}

      {/* Time Waveform Viewer */}
      <WaveformViewer
        machineId={machineId}
        jumpTo={waveformJumpTo}
        autoLoad={waveformAutoLoad}
        operatingRpm={rpmData?.operating_rpm ?? null}
        isVfd={rpmData?.is_vfd ?? false}
      />

      {/* Anomaly Score */}
      <AnomalyScoreChart
        machineId={machineId}
        days={90}
        onPointClick={(fileId, axisId, measuredAt, pointName) => {
          setWaveformJumpTo({ measured_at: measuredAt, point_name: pointName });
        }}
      />

      {/* Fault History */}
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border border-gray-200 shadow-sm">
        <div className="mb-4">
          <h3 className="text-gray-900 mb-2">Fault History</h3>
          <p className="text-gray-600">Historical analysis selections from published reports</p>
        </div>
        <div className="flex flex-wrap gap-3">
          {faultHistory.length > 0 ? (
            faultHistory.map((fault, index) => (
              <div key={index} className="px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                <span className="text-gray-900">{fault.analysis} {fault.count > 1 && <span className="text-gray-600">({fault.count})</span>}</span>
              </div>
            ))
          ) : (
            <p className="text-gray-500 italic">No fault history available</p>
          )}
        </div>
      </div>

      {/* Alarm History - 45 Day Rolling Window */}
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border border-gray-200 shadow-sm">
        <button
          onClick={() => setIsAlarmHistoryExpanded(!isAlarmHistoryExpanded)}
          className="w-full flex items-center justify-between mb-4 hover:opacity-80 transition-opacity"
        >
          <h3 className="text-gray-900">Alarm History (45 Days)</h3>
          <svg
            className={`w-5 h-5 text-gray-600 transition-transform ${isAlarmHistoryExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <span className="text-gray-600">Warning: {alarmHistory.filter(a => a.severity === 'medium').length}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <span className="text-gray-600">Critical: {alarmHistory.filter(a => a.severity === 'high').length}</span>
          </div>
          <div className="px-4 py-2 bg-gray-100 rounded-lg">
            <span className="text-gray-900">Total Alarms: {alarmHistory.length}</span>
          </div>
        </div>

        {isAlarmHistoryExpanded && (
          <div className="space-y-2 max-h-96 overflow-y-auto mt-4">
            {alarmHistory.map((alarm) => (
              <div
                key={alarm.id}
                className={`py-2 px-3 rounded-lg border ${
                  alarm.severity === 'high'
                    ? 'bg-red-50 border-red-200'
                    : 'bg-yellow-50 border-yellow-200'
                }`}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div
                        className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          alarm.severity === 'high' ? 'bg-red-500' : 'bg-yellow-500'
                        }`}
                      ></div>
                      <span className={`text-xs font-medium ${
                        alarm.severity === 'high' ? 'text-red-800' : 'text-yellow-800'
                      }`}>
                        {alarm.severity === 'high' ? 'Critical' : 'Warning'}
                      </span>
                      <span className="text-xs text-gray-500">{alarm.pointName} • {alarm.metric}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                      <span>Val: <span className="text-gray-700">{alarm.value}</span></span>
                      <span>Thr: <span className="text-gray-700">{alarm.threshold}</span></span>
                    </div>
                  </div>
                  <div className="text-right text-xs shrink-0">
                    <p className="text-gray-600">
                      {fmtEastern(alarm.timestamp, true, false)}
                    </p>
                    <p className="text-gray-400">
                      {fmtEastern(alarm.timestamp, false, true)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Rate of Change */}
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border border-gray-200 shadow-sm">
        <div className="mb-6">
          <h3 className="text-gray-900 mb-2">Rate of Change</h3>
        </div>

        {/* Skeleton while machine data is loading */}
        {machineLoading ? (
          <div className="space-y-2">
            {[0, 1, 2].map(i => (
              <div key={i} className="py-2 px-3 bg-white border border-gray-200 rounded-lg animate-pulse">
                <div className="h-3 bg-gray-200 rounded w-1/3 mb-2" />
                <div className="flex gap-2">
                  <div className="h-3 bg-gray-200 rounded flex-1" />
                  <div className="h-3 bg-gray-200 rounded flex-1" />
                </div>
              </div>
            ))}
          </div>
        ) : rocCachePending ? (
          <div className="flex items-start gap-3 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
            <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20A10 10 0 0012 2z" />
            </svg>
            Rate of change data will be available after the next poll cycle (within 1 hour).
          </div>
        ) : null}

        <div className="space-y-2" style={machineLoading || rocCachePending ? { display: 'none' } : {}}>
          {isRateOfChangeExpanded ? (
            rateOfChange.map((roc) => (
              <div key={roc.pointName} className="py-2 px-3 bg-white border border-gray-200 rounded-lg">
                <div className="mb-2">
                  <h4 className="text-sm text-gray-900">{roc.pointName}</h4>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    { label: 'Velocity', d: roc.velocity, unitCode: 2, fmt: (v: number) => v.toFixed(2) },
                    { label: 'Acceleration', d: roc.acceleration, unitCode: 0, fmt: (v: number) => v.toFixed(2) },
                  ].map(({ label, d, unitCode, fmt }) => {
                    const sparkVals = getSparklineValues(trendDataMap[unitCode], roc.pointName);
                    return (
                      <div key={label} className="flex items-center gap-2 py-1.5 px-2 bg-gray-50 rounded">
                        <span className="text-xs text-gray-500 w-20 shrink-0">{label}</span>
                        <Sparkline values={sparkVals} color={sparklineColor(d.percentChange)} />
                        <div className="flex items-center gap-1 ml-1">
                          {Math.abs(d.percentChange) < 1 ? (
                            <Minus className="w-3 h-3 text-gray-400" />
                          ) : d.percentChange > 0 ? (
                            <TrendingUp className="w-3 h-3 text-red-500" />
                          ) : (
                            <TrendingDown className="w-3 h-3 text-green-500" />
                          )}
                          <span className={`text-xs font-medium ${
                            Math.abs(d.percentChange) < 1 ? 'text-gray-700' : d.percentChange > 0 ? 'text-red-600' : 'text-green-600'
                          }`}>
                            {d.percentChange > 0 ? '+' : ''}{d.percentChange.toFixed(1)}%
                          </span>
                        </div>
                        <span className="text-xs text-gray-400 ml-auto">{fmt(d.current)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          ) : (
            top3Metrics.map((metric, index) => {
              const unitCodeMap: Record<string, number> = { Velocity: 2, Acceleration: 0, Envelope: 6, Temperature: 20 };
              const sparkVals = getSparklineValues(trendDataMap[unitCodeMap[metric.metric] ?? 0], metric.pointName);
              return (
                <div key={`${metric.pointName}-${metric.metric}-${index}`} className="py-2 px-3 bg-white border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-900">{metric.pointName}</span>
                    <span className="text-xs text-gray-500">{metric.metric}</span>
                  </div>

                  <div className="flex items-center gap-2 mt-1">
                    <Sparkline values={sparkVals} color={sparklineColor(metric.data.percentChange)} />
                    <div className="flex items-center gap-1">
                      {Math.abs(metric.data.percentChange) < 1 ? (
                        <Minus className="w-3 h-3 text-gray-400" />
                      ) : metric.data.percentChange > 0 ? (
                        <TrendingUp className="w-3 h-3 text-red-500" />
                      ) : (
                        <TrendingDown className="w-3 h-3 text-green-500" />
                      )}
                      <span className={`text-xs font-medium ${
                        Math.abs(metric.data.percentChange) < 1 ? 'text-gray-700' : metric.data.percentChange > 0 ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {metric.data.percentChange > 0 ? '+' : ''}{metric.data.percentChange.toFixed(1)}%
                      </span>
                    </div>
                    <span className="text-xs text-gray-400 ml-auto">
                      {fmtRoc(metric.data.current, metric.metric === 'Temperature')} / avg {fmtRoc(metric.data.average, metric.metric === 'Temperature')}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <button
          onClick={() => setIsRateOfChangeExpanded(!isRateOfChangeExpanded)}
          className="w-full flex items-center justify-between mt-4 hover:opacity-80 transition-opacity"
        >
          <h3 className="text-gray-900">
            {isRateOfChangeExpanded ? 'Show Less' : 'Show More'}
          </h3>
          <svg
            className={`w-5 h-5 text-gray-600 transition-transform ${isRateOfChangeExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Additional Metrics */}
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border border-gray-200 shadow-sm">
        <div className="flex items-start justify-between mb-4 gap-6">
          <div>
            <h3 className="text-gray-900">Real-time Point Metrics</h3>
            <p className="text-gray-400 text-xs mt-0.5">Peak amplitude per axis — latest reading</p>
          </div>
        </div>
        {machineLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-40 bg-gray-100 rounded-lg animate-pulse" />)}
          </div>
        ) : livePoints.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {livePoints.map((lp) => {
              const maxVel   = Math.max(lp.vel.H,   lp.vel.V,   lp.vel.A);
              const maxAccel = Math.max(lp.accel.H, lp.accel.V, lp.accel.A);
              const maxEnv   = Math.max(lp.env.H,   lp.env.V,   lp.env.A);
              const maxTemp  = Math.max(lp.temp.H,  lp.temp.V,  lp.temp.A);
              const ptSev    = pointAlarmSeverity.get(lp.name);
              const dotColor = ptSev === 'severe' ? '#EF4444' : ptSev === 'moderate' ? '#F59E0B' : '#10B981';
              // Build clickPoint: yellow=3σ alarm threshold, red=3σ×1.5
              const clickPoint: Point = {
                id: lp.name,
                name: lp.name,
                velocity:     lp.vel,
                acceleration: lp.accel,
                envelope:     lp.env,
                temperature:  lp.temp,
                status: lp.inAlarm ? 'high' : 'low',
                velocityThresholds:     { yellow: lp.velThreshold,   red: lp.velThresholdHigh   ?? lp.velThreshold * 1.5 },
                accelerationThresholds: { yellow: lp.accelThreshold, red: lp.accelThresholdHigh ?? lp.accelThreshold * 1.5 },
                envelopeThresholds:     { yellow: lp.envThreshold,   red: lp.envThresholdHigh   ?? lp.envThreshold * 1.5 },
                temperatureThresholds:  { yellow: 0, red: 0 },
                machineId: machineId,
                axisIds: lp.axisIds,
              };
              return (
                <button
                  key={lp.name}
                  onClick={() => setSelectedPoint(clickPoint)}
                  className="p-4 bg-white border border-gray-200 rounded-lg space-y-3 hover:bg-gray-50 hover:border-gray-300 transition-all cursor-pointer text-left"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-gray-900">{lp.name}</p>
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: dotColor }}
                    />
                  </div>
                  <div className="space-y-2">
                    <div>
                      <p className="text-gray-600">Velocity</p>
                      <p className="text-gray-900">{maxVel > 0 ? `${maxVel.toFixed(4)} in/s` : '—'}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Acceleration</p>
                      <p className="text-gray-900">{maxAccel > 0 ? `${maxAccel.toFixed(3)} G's` : '—'}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Envelope</p>
                      <p className="text-gray-900">{maxEnv > 0 ? `${maxEnv.toFixed(3)} G's` : '—'}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Temperature</p>
                      {maxTemp > 0 ? (
                        <p className="text-gray-900">{maxTemp.toFixed(1)} °C</p>
                      ) : sensorTempByPoint.has(lp.name) ? (
                        <p className="text-gray-900">{sensorTempByPoint.get(lp.name)!.toFixed(1)} °C <span className="text-xs text-gray-400">(sensor)</span></p>
                      ) : (
                        <p className="text-gray-900">—</p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-500 italic">No measurement data available</p>
        )}
      </div>

      {/* Sensors Section */}
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border border-gray-200 shadow-sm">
        <h3 className="text-gray-900 mb-4">Sensors</h3>
        {sensors.length === 0 ? (
          <p className="text-gray-400 text-sm italic">No sensors registered for this machine.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {sensors.map((sensor) => {
              const statusColor = sensor.status === 'active' ? '#22c55e' : '#ef4444';
              const serialColor = sensor.status === 'active' ? '#16a34a' : '#dc2626';
              const bv: number | null = sensor.battery_voltage ?? null;

              let tooltipLines: string[] = [];
              if (bv !== null) tooltipLines.push(`Battery: ${bv.toFixed(2)}V`);
              if (sensor.signal_strength != null) tooltipLines.push(`Signal: ${sensor.signal_strength} dBm`);
              if (sensor.sensor_temperature != null) tooltipLines.push(`Temp: ${(sensor.sensor_temperature * 9/5 + 32).toFixed(1)}°F`);
              if (sensor.last_data_at) {
                tooltipLines.push(`Last seen: ${fmtUtcToEastern(sensor.last_data_at)}`);
              }
              if (sensor.model) tooltipLines.push(`Model: ${sensor.model}`);

              return (
                <div
                  key={sensor.phantom_code}
                  className="p-4 bg-white border border-gray-200 rounded-lg space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-gray-900 font-medium">{sensor.point_name}</p>
                    <div className="relative group">
                      <div
                        className="w-2 h-2 rounded-full cursor-help"
                        style={{ backgroundColor: statusColor }}
                      />
                      {tooltipLines.length > 0 && (
                        <div className="absolute right-0 bottom-full mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                          {tooltipLines.map((line, i) => <div key={i}>{line}</div>)}
                          <div className="absolute top-full right-2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900" />
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Serial Number</p>
                    <p className="font-mono text-sm font-semibold" style={{ color: serialColor }}>
                      {sensor.phantom_code}
                    </p>
                  </div>
                  {(() => {
                    const history = sensorHealth[sensor.phantom_code] ?? [];
                    if (history.length < 2) return null;
                    const hasBattery = history.some(h => h.battery_voltage != null);
                    const hasSignal  = history.some(h => h.signal_strength != null);
                    if (!hasBattery && !hasSignal) return null;
                    const bvMin = 2.4, bvMax = 3.1;
                    return (
                      <div className="space-y-2 pt-1">
                        {hasBattery && (
                          <div>
                            <p className="text-gray-400 text-xs mb-0.5">
                              Battery — {history[history.length - 1].battery_voltage?.toFixed(2)}V
                            </p>
                            <ResponsiveContainer width="100%" height={36}>
                              <LineChart data={history} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
                                <Line
                                  type="monotone"
                                  dataKey="battery_voltage"
                                  stroke="#10b981"
                                  strokeWidth={1.5}
                                  dot={false}
                                  isAnimationActive={false}
                                />
                                <YAxis domain={[bvMin, bvMax]} hide />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        )}
                        {hasSignal && (
                          <div>
                            <p className="text-gray-400 text-xs mb-0.5">
                              Signal — {history[history.length - 1].signal_strength} dBm
                            </p>
                            <ResponsiveContainer width="100%" height={36}>
                              <LineChart data={history} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
                                <Line
                                  type="monotone"
                                  dataKey="signal_strength"
                                  stroke="#6366f1"
                                  strokeWidth={1.5}
                                  dot={false}
                                  isAnimationActive={false}
                                />
                                <YAxis domain={['dataMin - 5', 'dataMax + 5']} hide />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  <button
                    onClick={() => setEditingSensor(sensor)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-all group"
                    title="Edit sensor"
                  >
                    <Pencil className="w-4 h-4 text-gray-600 group-hover:text-gray-900" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Expanded Chart Modal */}
      {expandedChart && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-gray-900">{expandedChart.title}</h2>
              <button
                onClick={() => setExpandedChart(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-all"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="p-6">
              {expandedChart.chart}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
